import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavActions } from '../../hooks/useNavActions';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import {
  DEFAULT_DATE_RANGE,
} from '../../components/listDefaults';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { AuditTrailButton } from '../../components/AuditTrailButton';
import { FormTitle } from '../../components/FormTitle';
import { LoadTimeChip } from '../../components/LoadTimeChip';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { StepHint } from '../../components/StepHint/StepHint';
import { fmtLocalDate, fmtLocalDateTime, csvCell } from '../../utils/datetime';
import { exportRows } from '../../utils/exportData';
import {
  RollbackModal,
  type SdmsBatch,
} from './SdmsValidationActions';
import { SdmsPackageDetail, type SdmsPackageRow } from './SdmsPackageDetail';
// Result Validation (manual queue) drill-in detail — Sample Results + QC Results
// tabs + whole-batch Validate / Rollback / Project Notes (ALPACA ResultEntry_Validation).
import ResultValidationDetail, { type ResultValidationBatch } from './ResultValidationDetail';
// Result Tracking "Retrieve" drill-in — READ-ONLY Sample Results + QC Results for
// one batch (legacy ResultEntryQueryPanel Retrieve → SampleParameter ResultView).
import ResultTrackingDetail, { type ResultTrackingBatch } from './ResultTrackingDetail';
// Preview opens the SDMS spreadsheet directly (ALPACA parity) — same popup as
// Open Package → SDMS, but skipping the package-detail step.
import { SdmsSpreadsheet } from './SdmsSpreadsheet';
// Reuses the .dv-flow / .dv-fstep / .dv-fnote styles for the SDMS Result
// Validation / Approval list-view workflow stepper (same dark-pill flow bar
// shown in the Open Package overlay).
import './SdmsPackageDetail.css';
// (Merged 2026-07-07) QC Batches list-view UX — the per-row Data Package
// hyperlink, the New/Edit/Delete/Rollback/SDMS toolbar actions, and the inline
// QC Batch detail form (opened from New / row Edit).
import QcBatchDetailPage, { type QcBatchSeed } from './QcBatchDetailPage';
// Sample Prep Batch detail form (Prep Queue → New; Prep Batches → row Edit/view).
// Merged from the prep-feature branch 2026-07-17.
import PrepBatchDetailPage, { type PrepBatchSeed } from './PrepBatchDetailPage';
import { prepBatchApi } from './prepBatch.api';
import { qcBatchApi } from './qcBatch.api';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { useDeleteFlow, useConfirm, showDialog } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
// .dp-cell-link (the QC Batch grid's Data Package command-cell link) lives here.
import './qcBatch.css';

// ─────────────────────────────────────────────────────────────────────────────
// Shared list component for the eight BTLIMS XAF Analysis-module screens.
// Each screen passes a `kind` prop; the component picks the API endpoint and
// the column layout to render.  Data shapes:
//   - queue          → AnalysisQueueRow  (Sample Prep Queue, Analysis Queue)
//   - prep-batches   → PrepBatchRow
//   - batches        → AnalyticalBatchRow (QC Batches / Result Validation /
//                       SDMS Result Approval)
//   - result-tracking → ResultTrackingRow
// ─────────────────────────────────────────────────────────────────────────────

interface AnalysisQueueRow {
  testMethodId:        string;
  test:                string | null;
  method:              string | null;
  matrix:              string | null;
  pendingSampleCount:  number;
  oldestCollectedDate: string | null;
  newestCollectedDate: string | null;
  // Prep Queue extras
  prepMethod:          string | null;
  tierType:            string | null;
  prepSort?:           number | null;   // real PrepMethod Sort (prep-queue → seed)
  statusText:          string | null;
  statusId:            string | null;
  // Analysis Queue extras
  templateID:          number | null;
}

interface PrepBatchRow {
  id:              string;
  batchId:         string | null;
  jobIds:          string | null;
  test:            string | null;
  method:          string | null;
  matrix:          string | null;
  instrument:      string | null;
  tier:            number | null;
  sort:            number | null;
  temperature:     string | null;
  humidity:        string | null;
  remarks:         string | null;
  createdDate:     string | null;
  statusCode:      number;
  statusText:      string;
  statusId:        string | null;
  createdBy:       string | null;          // Employee.DisplayName ("E168044")
  lastUpdatedDate: string | null;
}

interface AnalyticalBatchRow {
  id:              string;
  batchId:         string | null;
  createdDate:     string | null;
  statusCode:      number;
  statusText:      string;
  statusId:        string | null;          // DV-1-X / QB-1-X
  templateID:      number | null;          // 1014 / 8074
  templateName:    string | null;          // SpreadSheetBuilder template name ("Phosphate")
  method:          string | null;
  test:            string | null;
  matrix:          string | null;
  jobIds:          string | null;
  instrument:      string | null;
  noruns:          number | null;
  reviewedBy:      string | null;
  reviewedDate:    string | null;
  verifiedBy:      string | null;
  verifiedByName:  string | null;          // approver E-code, resolved (SDMS Result Approval)
  verifiedDate:    string | null;
  rolledBackBy:    string | null;
  rolledBackDate:  string | null;
  rollbackReason:  string | null;
  // SDMS Result Validation extras (GoLive parity)
  validatedByList:  string | null;     // "E155503, E172600"
  validatedDateMax: string | null;
  // "Submitted By" (Batch Information header) — distinct from ValidatedByList.
  submittedByList:  string | null;
  // "Date Submitted" — ALPACA's MAX(sample SubmittedDate), NOT the batch CreatedDate.
  submittedDateMax: string | null;
  // (Merged 2026-07-07) Per-row Data Package availability (legacy
  // DataPackageCheckList gate) — drives the QC Batches "Data Package" link.
  dataPackageEnabled: boolean;
}

// ResultTrackingRow now mirrors GoLive's QC-batch-level shape exactly.
interface ResultTrackingRow {
  id:            string;
  qcBatchID:     string | null;
  client:        string | null;
  projectID:     string | null;
  jobID:         string | null;
  createdDate:   string | null;
  statusCode:    number;
  statusText:    string;
}

// Result Validation (manual) list row — a SUPERSET of both "Search By" grains
// (ALPACA DataReviewQueryPanelController): QCBatchID mode = one row per QC batch
// (QCBatchID · Client · ProjectID · CreatedDate · Status); JobID mode = one row
// per job (JobID · ProjectID · ProjectName · Client · ReceivedDate).  The API
// returns the superset; the unused fields are null in each mode.
interface ResultValidationListRow {
  id:            string;
  /** Samplecheckin Oid for this row's job — QCBatchID mode is one row per
   *  (batch, job); the detail scopes the Sample tab to this job. */
  jobOid:        string | null;
  qcBatchID:     string | null;
  client:        string | null;
  projectID:     string | null;
  projectName:   string | null;
  jobID:         string | null;
  createdDate:   string | null;
  receivedDate:  string | null;
  statusCode:    number;
  statusText:    string;
}

export type AnalysisKind =
  | 'prep-queue' | 'prep-batches'
  | 'analysis-queue' | 'qc-batches'
  | 'result-validation' | 'result-approval' | 'sdms-approval'  // (Merged 2026-07-07)
  | 'result-validation-manual'   // "Result Validation" — manual result-entry Level-2 queue
  | 'result-tracking';

// kind → the form's nav id, so the shared <FormTitle> sources the icon/colour
// from the SAME navData entry as the sidebar (CLAUDE.md §18.6, task 3).
const KIND_NAV_ID: Record<AnalysisKind, string> = {
  'prep-queue':        'preplqueue',
  'prep-batches':      'prepbatches',
  'analysis-queue':    'analysisq',
  'qc-batches':        'qcbatches',
  'result-validation': 'sdmsval',
  'result-approval':   'rappr',
  'sdms-approval':     'sdmsappr',   // (Merged 2026-07-07)
  'result-validation-manual': 'rval',
  'result-tracking':   'restrack',
};

interface AnalysisListPageProps {
  kind:      AnalysisKind;
  title:     string;
  subtitle:  string;
  /** Breadcrumb group label (e.g. 'Sample Preparation', 'Analysis', 'Data Review'). */
  group:     string;
  /** (Merged 2026-07-07) Navigate to another nav page (AppShell.open) — used by
   *  the QC Batches list's New button to jump to the Analysis Queue (where QC
   *  batches are started). */
  onOpenNav?: (page: string) => void;
}

const ENDPOINT: Record<AnalysisKind, string> = {
  'prep-queue':         '/api/v1/analysis/prep-queue',
  'prep-batches':       '/api/v1/analysis/prep-batches',
  'analysis-queue':     '/api/v1/analysis/analysis-queue',
  'qc-batches':         '/api/v1/analysis/qc-batches',
  'result-validation':  '/api/v1/analysis/result-validation',
  'result-approval':    '/api/v1/analysis/result-approval',
  'sdms-approval':      '/api/v1/analysis/sdms-result-approval',   // (Merged 2026-07-07)
  'result-validation-manual': '/api/v1/analysis/manual-result-validation',
  'result-tracking':    '/api/v1/analysis/result-tracking',
};

const DATE_OPTS = ['1M', '3M', '6M', '1Y', 'All'] as const;
type DateRangeKey = typeof DATE_OPTS[number];

/** Compact CSV export — used by the SampleDetails picker modal's Export button.
 *  (The main list grid exports via gridExportMatrix through AppToolbar; this is
 *  a local helper only for the modal, which isn't backed by <AppDataGrid>.) */
function exportCsv(rows: unknown[], filename: string) {
  if (rows.length === 0) return;
  const first = rows[0] as Record<string, unknown>;
  const keys = Object.keys(first);
  // Route each cell through csvCell so ISO date/datetime cells export as
  // MM/DD/YYYY (matching the grid) instead of raw ISO; non-dates pass through.
  void exportRows(rows, keys.map(k => ({
    header: k,
    value: (r: unknown) => csvCell((r as Record<string, unknown>)[k]),
  })), { filename });
}

// Render via the shared §14b.6 helpers (local tz, 24-hour, no AM/PM); keep the
// '—' placeholder for empty/unparseable values used in grid cells + CSV export.
function fmtDate(v?: string | null): string {
  return v ? (fmtLocalDate(v) || '—') : '—';
}
function fmtDateTime(v?: string | null): string {
  return v ? (fmtLocalDateTime(v) || '—') : '—';
}

function sinceFromKey(k: DateRangeKey): string | undefined {
  // "All" = no date limit.  The batch SPs floor a NULL @SinceDate to a 1-month
  // DEFAULT (a perf guard: `ISNULL(@SinceDate, DATEADD(MONTH,-1,GETDATE()))`), so
  // sending nothing would make "All" return FEWER rows than "3M".  Send an
  // explicit far-past floor instead so "All" truly returns every record.
  if (k === 'All') return '1900-01-01';
  const now = new Date();
  const map: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()),
    '3M': () => new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    '6M': () => new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
    '1Y': () => new Date(now.getFullYear() - 1, now.getMonth(),  now.getDate()),
  };
  const d = map[k as Exclude<DateRangeKey, 'All'>]();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Whether the dataset for this kind supports a date filter.
function supportsDate(kind: AnalysisKind): boolean {
  return kind === 'prep-batches' || kind === 'qc-batches' || kind === 'result-tracking'
      || kind === 'result-validation' || kind === 'sdms-approval'   // (Merged 2026-07-07)
      || kind === 'result-validation-manual'
      || kind === 'result-approval';   // manual Result Approval — same Date Range picker
}

// Column lists below match GoLive ALPACA LIMS exactly (audited 2026-05-24).
// We keep our existing DTOs and just relabel + reorder visible columns.
// Where GoLive shows a field we don't have, we emit "—".
//
// Each kind builds AppColumn[] for the shared grid.  Status columns use
// type:'status' (shared statusPill mapping); date columns render through the
// page's existing fmt* helpers so the on-screen text is byte-for-byte the same
// as before the migration.  ID/text columns render "—" for null, matching the
// previous table look (cells stay black — the shared grid colours status only).

const PREP_QUEUE_COLS: AppColumn<AnalysisQueueRow>[] = [
  { field: 'Test',         header: 'Test',         text: r => r.test ?? '',   render: r => r.test || '—' },
  { field: 'SampleMatrix', header: 'SampleMatrix', text: r => r.matrix ?? '', render: r => r.matrix || '—' },
  { field: 'PrepMethod',   header: 'PrepMethod',   text: r => r.prepMethod ?? '', render: r => r.prepMethod || '—' },
  { field: 'Method',       header: 'Method',       text: r => r.method ?? '', render: r => r.method || '—' },
  { field: 'TierType',     header: 'TierType',     text: r => r.tierType ?? '', render: r => r.tierType || '—' },
  { field: 'NumSamples',   header: '#Samples',     type: 'number', text: r => String(r.pendingSampleCount ?? ''), value: r => r.pendingSampleCount ?? 0 },
  { field: 'Status',       header: 'Status',       type: 'status', text: r => r.statusText ?? 'Pending Tier2 Prep' },
  { field: 'StatusID',     header: 'StatusID',     text: r => r.statusId ?? 'PB-2-1', render: r => r.statusId ?? 'PB-2-1' },
];

const ANALYSIS_QUEUE_COLS: AppColumn<AnalysisQueueRow>[] = [
  { field: 'Test',          header: 'Test',          text: r => r.test ?? '',   render: r => r.test || '—' },
  { field: 'Method',        header: 'Method',        text: r => r.method ?? '', render: r => r.method || '—' },
  { field: 'Matrix',        header: 'Matrix',        text: r => r.matrix ?? '', render: r => r.matrix || '—' },
  { field: 'Template',      header: 'Template',      text: r => r.templateID == null ? '' : String(r.templateID), render: r => r.templateID == null ? '—' : String(r.templateID) },
  // GoLive shows two distinct columns: a numeric count and a clickable
  // "Sample Details" link.  We render the count plain (no "pending" word)
  // and a chevron column that opens the same drill-down panel Prep Queue
  // uses — the whole row is clickable via onRowClick further down.
  { field: 'NumSamples',    header: '#Samples',      type: 'number', text: r => String(r.pendingSampleCount ?? 0), value: r => r.pendingSampleCount ?? 0 },
  { field: 'SampleDetails', header: 'Sample Details', sortable: false, filterable: false, text: () => 'View details →', render: () => 'View details →' },
];

const PREP_BATCH_COLS: AppColumn<PrepBatchRow>[] = [
  { field: 'PrepBatchID', header: 'PrepBatchID', text: r => r.batchId ?? '', render: r => r.batchId || '—' },
  { field: 'Matrix',      header: 'Matrix',      text: r => r.matrix ?? '',  render: r => r.matrix || '—' },
  { field: 'Test',        header: 'Test',        text: r => r.test ?? '',    render: r => r.test || '—' },
  { field: 'Method',      header: 'Method',      text: r => r.method ?? '',  render: r => r.method || '—' },
  { field: 'Instrument',  header: 'Instrument',  text: r => r.instrument ?? '', render: r => r.instrument || '—' },
  { field: 'DateCreated', header: 'DateCreated', text: r => fmtDateTime(r.createdDate), render: r => fmtDateTime(r.createdDate) },
  { field: 'CreatedBy',   header: 'CreatedBy',   text: r => r.createdBy ?? '', render: r => r.createdBy || '—' },
  { field: 'Status',      header: 'Status',      type: 'status', text: r => r.statusText ?? '' },
  { field: 'StatusID',    header: 'StatusID',    text: r => r.statusId ?? '', render: r => r.statusId || '—' },
];

// (Merged 2026-07-07) QC Batch list columns.  The leading "Data Package" column
// is a per-row command cell (legacy list-view hyperlink): a clickable link when
// the batch's Test has a DataPackageCheckList (r.dataPackageEnabled), otherwise a
// greyed, non-clickable label.  onDataPackage runs the action (deferred — info
// toast until the module lands).
function qcBatchCols(onDataPackage: (r: AnalyticalBatchRow) => void): AppColumn<AnalyticalBatchRow>[] {
  return [
    // Data Package command-cell link — FIRST column (user request), sized to its
    // caption width (no sort/filter chrome — it's an action cell, not data).
    {
      field: 'DataPackage', header: 'DataPackage', sortable: false, filterable: false,
      width: 120, minWidth: 120,
      text: r => r.dataPackageEnabled ? 'Available' : '',
      render: r => r.dataPackageEnabled
        ? <a className="dp-cell-link"
             onClick={e => { e.stopPropagation(); onDataPackage(r); }}>Data Package</a>
        : <span className="dp-cell-link dp-cell-link--off">Data Package</span>,
    },
    { field: 'QCBatchID',  header: 'QCBatchID',  text: r => r.batchId ?? '', render: r => r.batchId || '—' },
    { field: 'Matrix',     header: 'Matrix',     text: r => r.matrix ?? '',  render: r => r.matrix || '—' },
    { field: 'Test',       header: 'Test',       text: r => r.test ?? '',    render: r => r.test || '—' },
    { field: 'Method',     header: 'Method',     text: r => r.method ?? '',  render: r => r.method || '—' },
    { field: 'Status',     header: 'Status',     type: 'status', text: r => r.statusText ?? '' },
    { field: 'StatusID',   header: 'StatusID',   text: r => r.statusId ?? '', render: r => r.statusId || '—' },
    { field: 'TemplateID', header: 'TemplateID', text: r => r.templateID == null ? '' : String(r.templateID), render: r => r.templateID == null ? '—' : String(r.templateID) },
  ];
}

// (Merged 2026-07-07) Pull the API's friendly error message out of an axios error
// (guard messages from the QC Delete/Rollback endpoints), falling back to a default.
function qcApiErr(e: unknown, fallback: string): string {
  const er = e as { response?: { data?: { error?: { message?: string } } } };
  return er.response?.data?.error?.message ?? fallback;
}

/** Result Tracking columns swap their first ID column based on the
 *  Search By dropdown — GoLive shows JobID when "JobID" is selected,
 *  QCBatchID otherwise.  This matches the screenshot from 2026-05-25. */
function resultTrackingCols(searchBy: 'QCBatchID' | 'JOBID'): AppColumn<ResultTrackingRow>[] {
  const idCol: AppColumn<ResultTrackingRow> = searchBy === 'JOBID'
    ? { field: 'JobID',     header: 'JobID',     text: r => r.jobID ?? '',     render: r => r.jobID || '—' }
    : { field: 'QCBatchID', header: 'QCBatchID', text: r => r.qcBatchID ?? '', render: r => r.qcBatchID || '—' };
  return [
    idCol,
    { field: 'Client',      header: 'Client',      text: r => r.client ?? '',    render: r => r.client || '—' },
    { field: 'ProjectID',   header: 'ProjectID',   text: r => r.projectID ?? '', render: r => r.projectID || '—' },
    { field: 'CreatedDate', header: 'CreatedDate', text: r => fmtDate(r.createdDate), render: r => fmtDate(r.createdDate) },
    { field: 'Status',      header: 'Status',      type: 'status', text: r => r.statusText ?? '' },
  ];
}

/** Result Validation (manual) — QCBatchID mode columns (ALPACA
 *  `QCBatch_ListView_ResultValidation_Filter`, base = SampleParameter):
 *  QCBatchID · Client · ProjectID · CreatedDate · Status.  One row per QC batch. */
function resultValidationQcCols(): AppColumn<ResultValidationListRow>[] {
  return [
    { field: 'QCBatchID',   header: 'QCBatchID',   text: r => r.qcBatchID ?? '', render: r => r.qcBatchID || '—' },
    { field: 'Client',      header: 'Client',      text: r => r.client ?? '',    render: r => r.client || '—' },
    // ProjectID renders BLANK when empty (user request 2026-07-10), not '—'.
    { field: 'ProjectID',   header: 'ProjectID',   text: r => r.projectID ?? '', render: r => r.projectID ?? '' },
    { field: 'CreatedDate', header: 'CreatedDate', text: r => fmtDate(r.createdDate), render: r => fmtDate(r.createdDate) },
    { field: 'Status',      header: 'Status',      type: 'status', text: r => r.statusText ?? '' },
  ];
}

/** Result Validation (manual) — JobID mode columns (ALPACA
 *  `Samplecheckin_ListView_ResultValidation`, base = Samplecheckin):
 *  JobID · ProjectID · ProjectName · Client · ReceivedDate.  One row per job.
 *  ReceivedDate uses the legacy "{0:g}" general format = MM/DD/YYYY HH:mm. */
function resultValidationJobCols(): AppColumn<ResultValidationListRow>[] {
  return [
    { field: 'JobID',        header: 'JobID',        text: r => r.jobID ?? '',       render: r => r.jobID || '—' },
    // ProjectID / ProjectName render BLANK when empty (user request 2026-07-10;
    // ALPACA leaves them blank), not the '—' placeholder.
    { field: 'ProjectID',    header: 'ProjectID',    text: r => r.projectID ?? '',   render: r => r.projectID ?? '' },
    { field: 'ProjectName',  header: 'ProjectName',  text: r => r.projectName ?? '', render: r => r.projectName ?? '' },
    { field: 'Client',       header: 'Client',       text: r => r.client ?? '',      render: r => r.client || '—' },
    { field: 'ReceivedDate', header: 'ReceivedDate', text: r => fmtDateTime(r.receivedDate), render: r => fmtDateTime(r.receivedDate) },
  ];
}

// SDMS Result Validation columns — DataPackage "Open Package" link first
// (XAF parity, see DataReviewController.BatchReviewDataPackageHyperLink),
// followed by a per-row Edit icon (legacy SpreadSheetEntry_AnalyticalBatch
// _ListView_RawDataLevel2BatchReview has AllowEdit="True" InlineEditMode="Batch").
// Callbacks are passed in so the column factory stays pure.
// (Merged 2026-07-07) — the `approval` flag drives SDMS Result Approval (Level-3):
// it hides the per-row Edit icon (ALPACA read-only parity) and appends
// DateVerified / VerifiedBy columns.
function resultValidationCols(
  onOpenPackage: (row: AnalyticalBatchRow) => void,
  onEditRow:     (row: AnalyticalBatchRow) => void,
  onPreviewRow:  (row: AnalyticalBatchRow) => void,
  approval = false,   // SDMS Result Approval (Level-3) appends DateVerified/VerifiedBy
): AppColumn<AnalyticalBatchRow>[] {
  const iconBtn: CSSProperties = {
    border: 'none', background: 'transparent',
    color: 'var(--ai-icon-edit)', cursor: 'pointer',
    padding: 2, borderRadius: 4, lineHeight: 0,
  };
  const cols: AppColumn<AnalyticalBatchRow>[] = [
    {
      // Actions sit BETWEEN the multi-select checkbox and DataPackage — a per-row
      // Edit (opens the detail) + Preview (opens the SDMS result image), matching
      // the prototype's Actions column (pencil + eye).
      field: 'Actions', header: 'Actions', sortable: false, filterable: false,
      width: 74, align: 'center',
      text: () => '',
      render: r => (
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          {/* Edit (opens the detail) — SDMS Result VALIDATION only.  ALPACA's
              SDMS Result Approval (Level-3 · DataVerify) grid is READ-ONLY, so
              it shows no edit icon there (Merged 2026-07-07). */}
          {!approval && (
            <button onClick={e => { e.preventDefault(); e.stopPropagation(); onEditRow(r); }}
              title="Edit this batch" aria-label="Edit" style={iconBtn}>
              <i className="ti ti-edit" style={{ fontSize: 16 }} />
            </button>
          )}
          <button onClick={e => { e.preventDefault(); e.stopPropagation(); onPreviewRow(r); }}
            title="Preview SDMS result image" aria-label="Preview" style={iconBtn}>
            <i className="ti ti-eye" style={{ fontSize: 16 }} />
          </button>
        </span>
      ),
    },
    {
      field: 'DataPackage', header: 'DataPackage', sortable: false, filterable: false,
      text: () => 'Open Package',
      render: r => (
        <a href="#" onClick={e => { e.preventDefault(); onOpenPackage(r); }}
           className="sa-openpkg"
           style={{ color: 'var(--brand-accent-hover, var(--ai-orange-hover))', fontWeight: 600, textDecoration: 'none' }}>
          {/* Prototype parity: steel-blue package icon + orange-dk text
              (.pkg a{color:var(--orange-dk)} — SDMS-Result-Validation-Prototype (7)). */}
          <i className="ti ti-package" style={{ marginRight: 5, color: 'var(--ai-blue)' }} />
          Open Package
        </a>
      ),
    },
    { field: 'QCBatchID',     header: 'QCBatchID',     text: r => r.batchId ?? '', render: r => r.batchId || '—' },
    // DateCreated: legacy xafml (Model.xafml line 59368) has NO DisplayFormat
    // on this column → date-only display.  DateValidated (line 59378) is the
    // only date column on this view that specifies MM/dd/yyyy HH:mm.
    { field: 'DateCreated',   header: 'DateCreated',   text: r => fmtDate(r.createdDate), render: r => fmtDate(r.createdDate) },
    { field: 'Test',          header: 'Test',          text: r => r.test ?? '',    render: r => r.test || '—' },
    { field: 'Method',        header: 'Method',        text: r => r.method ?? '',  render: r => r.method || '—' },
    { field: 'Status',        header: 'Status',        type: 'status', text: r => r.statusText ?? '' },
    // StatusID: blank when the row has no TemplateID (i.e. TemplateID = 0 →
    // uninitialised batch — the legacy stores a mojibake em-dash in that case
    // which renders as "â€"", so we hide it) or when the value itself is empty.
    { field: 'StatusID',      header: 'StatusID',
      text:   r => (r.templateID === 0 || !r.statusId) ? '' : r.statusId,
      render: r => (r.templateID === 0 || !r.statusId) ? '' : r.statusId },
    // TemplateID: blank for 0 (uninitialised) and null; show the number otherwise.
    { field: 'TemplateID',    header: 'TemplateID',
      text:   r => (r.templateID == null || r.templateID === 0) ? '' : String(r.templateID),
      render: r => (r.templateID == null || r.templateID === 0) ? '' : String(r.templateID) },
    // GoLive SDMS Result Validation pulls DateValidated/ValidatedBy from
    // SampleParameter aggregation (CSV of distinct E-codes + MAX validated
    // date).  Empty cells render blank — no em-dash placeholder — for pending
    // batches (matches legacy display for these two columns specifically).
    { field: 'DateValidated', header: 'DateValidated',
      text:   r => r.validatedDateMax ? (fmtLocalDateTime(r.validatedDateMax) || '') : '',
      render: r => r.validatedDateMax ? (fmtLocalDateTime(r.validatedDateMax) || '') : '' },
    { field: 'ValidatedBy',   header: 'ValidatedBy',
      text:   r => r.validatedByList ?? '',
      render: r => r.validatedByList ?? '' },
  ];
  // SDMS Result Approval (Level-3, RawDataLevel3BatchReview) shows two extra
  // columns after ValidatedBy — the verification/approval stamp, blank until the
  // batch is approved (matches ALPACA's screenshot: DateVerified / VerifiedBy).
  // (Merged 2026-07-07.)
  if (approval) cols.push(
    { field: 'DateVerified', header: 'DateVerified',
      text:   r => r.verifiedDate ? (fmtLocalDateTime(r.verifiedDate) || '') : '',
      render: r => r.verifiedDate ? (fmtLocalDateTime(r.verifiedDate) || '') : '' },
    { field: 'VerifiedBy',   header: 'VerifiedBy',
      text:   r => r.verifiedByName ?? '',
      render: r => r.verifiedByName ?? '' },
  );
  return cols;
}

function kindToFormId(k: string): string {
  switch (k) {
    case 'result-validation':        return 'sdmsval';
    case 'sdms-approval':            return 'sdmsappr';
    case 'result-validation-manual': return 'rval';
    case 'result-approval':          return 'rappr';
    default:                         return '';
  }
}

export default function AnalysisListPage({ kind, title, subtitle, group, onOpenNav }: AnalysisListPageProps) {
  const perm = useNavActions(kindToFormId(kind));
  const [rows, setRows]       = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [error, setError]     = useState<string | null>(null);
  // GoLive Date Filter Category default (Analysis Entry Model + Analysis
  // Review Level both ship as 3M).
  const [dateRange, setDateRange] = useState<DateRangeKey>(
    (kind === 'result-validation' || kind === 'result-approval' || kind === 'sdms-approval'  // (Merged 2026-07-07)
      || kind === 'result-validation-manual'
      ? DEFAULT_DATE_RANGE.AnalysisReview
      : DEFAULT_DATE_RANGE.AnalysisEntry) as DateRangeKey,
  );
  const [search,   setSearch]   = useState('');
  const [statusChip, setStatusChip] = useState<string>('all');
  const [gridMaxed, setGridMaxed] = useState(false);   // SDMS list: full-screen the grid
  // Result Tracking / Result Validation / Result Approval: GoLive's Search By
  // dropdown.  Result Validation + Approval both default to QCBatchID (the ALPACA
  // Data-Review default) with a JOBID toggle.
  const [searchBy, setSearchBy] = useState<'QCBatchID' | 'JOBID'>('QCBatchID');
  // Prep Queue / Analysis Queue drill-down — the row opens a slide-over panel.
  const [selectedQueue, setSelectedQueue] = useState<AnalysisQueueRow | null>(null);

  // ── QC Batches list — inline detail form + single-select toolbar actions ────
  // (Merged 2026-07-07) Clicking New / a row Edit swaps the list for the inline
  // QcBatchDetailPage (seeded from the selected batch); Edit/Delete/Rollback
  // operate on the single ticked batch.
  const [qcBatchSeed, setQcBatchSeed] = useState<QcBatchSeed | null>(null);
  // Sample Prep Queue "New" swaps the list for the inline PrepBatchDetailPage,
  // seeded from the selected prep-queue row (TestMethod + Tier).
  const [prepBatchSeed, setPrepBatchSeed] = useState<PrepBatchSeed | null>(null);
  // QC Batches list — single-row checkbox selection driving the Edit/Delete/
  // Rollback toolbar actions (legacy SpreadSheetEntry_AnalyticalBatch_ListView
  // actions). Radio-like one-at-a-time model.
  const [qcSel, setQcSel] = useState<Set<string | number>>(new Set());
  // Analysis Queue single-select → drives the "Create QC Batch" action (New).
  // Completes the QC create flow: pick a test-method row here, then New opens
  // the QC Batch form (create mode) for it. (Merged 2026-07-07.)
  const [analysisSel, setAnalysisSel] = useState<Set<string | number>>(new Set());
  const runDelete = useDeleteFlow();
  const { prompt } = useConfirm();
  const notify = useNotify();

  // ── SDMS Result Validation / SDMS Result Approval: selection + modal state ─
  // (Merged 2026-07-07) SDMS Result Approval (Level-3, kind 'sdms-approval') reuses
  // the ENTIRE Result Validation machinery (selection · Open Package · toolbar ·
  // island stepper · detail overlay); the ONLY differences are the labels + the
  // terminal action ("Approve" not "Validate"), keyed off `isApproval`.  NOTE:
  // this is a SEPARATE form from the legacy job-level "Result Approval" (kind
  // 'result-approval'), which is left exactly as it was.
  const isApproval = kind === 'sdms-approval';
  const isSdms = kind === 'result-validation' || isApproval;
  // "Result Validation" (manual result-entry queue) — a SEPARATE, self-contained
  // selectable list with Validate / Rollback toolbar actions (NOT the SDMS
  // Open-Package / stepper machinery).  Reuses selectedIds for row selection.
  const isManualVal = kind === 'result-validation-manual';
  // "Result Approval" (manual result-entry Level-3 queue) — the DIRECT parallel of
  // the manual Result Validation list, one stage later.  Shares ALL the list
  // machinery (Search By · columns · row-click select · Load · detail); the only
  // differences (Status=2, Approve, ApprovedBy/Date) live in the shared detail via
  // its `mode='approval'` prop.  `isManualDR` = "either manual Data-Review stage".
  const isManualAppr = kind === 'result-approval';
  const isManualDR   = isManualVal || isManualAppr;
  // Result Tracking — read-only list with a single-select checkbox + "Retrieve"
  // drill-in (reuses `selectedIds`; the kinds are mutually exclusive).
  const isTracking   = kind === 'result-tracking';
  // Row-click drills into the Result Validation detail (Sample + QC tabs).
  const [rvDetail, setRvDetail] = useState<ResultValidationBatch | null>(null);
  // Result Tracking "Retrieve" opens the read-only batch-results drill-in.
  const [rtDetail, setRtDetail] = useState<ResultTrackingBatch | null>(null);
  // Dock into the shared topbar stepper slot AppShell actually renders
  // (#ai-topbar-stepper). The SDMS/QC bases used a differently-named slot that
  // doesn't exist here, so the SDMS workflow stepper never appeared. (Fix 2026-07-07.)
  const [bannerSlot, setBannerSlot] = useState<HTMLElement | null>(null);
  useEffect(() => { setBannerSlot(document.getElementById('ai-topbar-stepper')); }, []);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [rollbackOpen,  setRollbackOpen]  = useState(false);
  // §13 Notifications (standing rule 7): rollback outcomes go through the shared
  // global service (useNotify) — success → transient top banner (notify.success),
  // refusal → centered blocking modal (notify.error / .warning auto-route via
  // NotificationProvider). No per-form banner state/JSX. Wording keeps ALPACA
  // parity ("Rollbacked successfully." = DataReviewController ShowMessage).
  const [previewTarget, setPreviewTarget] = useState<AnalyticalBatchRow | null>(null);
  const [pkgTarget,     setPkgTarget]     = useState<AnalyticalBatchRow | null>(null);
  // Reset selection whenever the kind or dataset changes — including a Search By
  // flip on the manual queue (JOBID rows are jobs, QCBatchID rows are batches, so
  // a stale selected id would never match the swapped grid).
  useEffect(() => { setSelectedIds(new Set()); setQcSel(new Set()); setAnalysisSel(new Set()); }, [kind, searchBy]);

  // Browser Back returns from the Open Package detail overlay to the list.
  // The detail is an on-page overlay (same URL, no route change), so without
  // this the Back button would skip past it to the previous page.  We push a
  // history entry when it opens and close the overlay on popstate (Back); the
  // detail's X closes via closePackage → history.back() so the entry pops
  // cleanly and Back/X behave identically.
  useEffect(() => {
    if (!pkgTarget) return;
    window.history.pushState({ sdmsPkg: true }, '');
    const onPop = () => setPkgTarget(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [pkgTarget]);
  const closePackage = useCallback(() => {
    if ((window.history.state as { sdmsPkg?: boolean } | null)?.sdmsPkg) window.history.back();
    else setPkgTarget(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (supportsDate(kind)) {
        const since = sinceFromKey(dateRange);
        if (since) params.sinceDate = since;
      }
      if (kind === 'result-tracking' || kind === 'result-approval'
          || kind === 'result-validation-manual') {
        params.searchBy = searchBy;
        if (search.trim()) params.search = search.trim();
      }
      const res = await api.get(ENDPOINT[kind], { params });
      setRows((res.data as { data: unknown[] }).data ?? []);
    } catch (e) {
      console.error(e);
      setRows([]);
      setError('Failed to load — check the API logs.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [kind, dateRange, searchBy, search]);

  useEffect(() => { void load(); }, [load]);
  // Reset status chip when the kind changes (different columns / data).
  useEffect(() => { setStatusChip('all'); }, [kind]);
  // Restore the maximized grid on Escape, and on navigation to another screen.
  useEffect(() => { setGridMaxed(false); }, [kind]);
  useEffect(() => {
    if (!gridMaxed) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setGridMaxed(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gridMaxed]);

  // Does this kind have a status column suitable for chip filtering?
  // Includes prep-queue + result-tracking (GoLive parity — both grids
  // ship with the same status chip row even though they currently only
  // have one common status; future SP additions will surface naturally
  // because the chips are derived from whatever statusText values the
  // API returns).
  const hasStatusChips =
    kind === 'prep-queue'    || kind === 'prep-batches'
    || kind === 'qc-batches' || kind === 'result-tracking'
    || kind === 'result-validation' || kind === 'result-approval'
    || kind === 'result-validation-manual';

  // ── Free-text search across the columns the user looks at most ──────────
  const searchFiltered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r => {
      // Index into all string-y fields on whatever row shape we have
      return Object.values(r as Record<string, unknown>).some(v =>
        typeof v === 'string' && v.toLowerCase().includes(term),
      );
    });
  }, [rows, search]);

  // ── Status chip filter (only meaningful when hasStatusChips) ────────────
  const filtered = useMemo(() => {
    if (!hasStatusChips || statusChip === 'all') return searchFiltered;
    return searchFiltered.filter(r => (r as { statusText?: string }).statusText === statusChip);
  }, [searchFiltered, statusChip, hasStatusChips]);

  // ── Status chip definitions derived from the loaded data ────────────────
  const statusChips = useMemo(() => {
    if (!hasStatusChips) return [] as [string, number][];
    const m = new Map<string, number>();
    for (const r of searchFiltered) {
      const s = (r as { statusText?: string }).statusText;
      if (!s) continue;
      m.set(s, (m.get(s) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [searchFiltered, hasStatusChips]);

  // Per-kind shared-grid columns.  Result Tracking swaps its first column based
  // on the Search By dropdown, so that branch depends on `searchBy`.
  // Each kind returns a column set typed to its own row shape; `any` unifies them
  // in one memo without a sprawling union (the column defs stay individually typed).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cols = useMemo<AppColumn<any>[]>(() => {
    switch (kind) {
      case 'prep-queue':        return PREP_QUEUE_COLS;
      case 'analysis-queue':    return ANALYSIS_QUEUE_COLS;
      case 'prep-batches':      return PREP_BATCH_COLS;
      // (Merged 2026-07-07) Data Package action is deferred (the module isn't
      // built) — the enabled link shows a "coming soon" notice; the per-row
      // enable/disable is real (r.dataPackageEnabled).
      case 'qc-batches':        return qcBatchCols(() => notify.info('Data Package assembly is coming in a later phase.'));
      case 'result-validation': return resultValidationCols(
        // Both the DataPackage link AND the per-row Edit icon open the SAME
        // detail view (CLAUDE.md rule 12; legacy parity — clicking Edit in the
        // legacy ALPACA list view also opens the full Batch Information /
        // Batch Results / Check Points / Review Log / Attachments form).
        r => setPkgTarget(r as AnalyticalBatchRow),
        r => setPkgTarget(r as AnalyticalBatchRow),
        // Per-row Preview (eye) → the SDMS result-image PreviewModal, directly
        // on that row (no need to tick the checkbox + use the toolbar button).
        r => setPreviewTarget(r as AnalyticalBatchRow),
      );
      // (Merged 2026-07-07) SDMS Result Approval (Level-3) — the SAME batch grid as
      // Result Validation (Open Package · Preview, NO Edit), plus DateVerified /
      // VerifiedBy.  Opens SdmsPackageDetail in approval mode.
      case 'sdms-approval':     return resultValidationCols(
        r => setPkgTarget(r as AnalyticalBatchRow),
        r => setPkgTarget(r as AnalyticalBatchRow),
        r => setPreviewTarget(r as AnalyticalBatchRow),
        true,   // approval → hide Edit + append DateVerified / VerifiedBy
      );
      // "Result Validation" + "Result Approval" (manual queues) — the Search By
      // dropdown SWAPS the whole grid (ALPACA ResultValidateSearchBy_SelectedItemChanged):
      // JOBID → job-level columns (Samplecheckin_ListView_Result{Validation|Approval}),
      // QCBatchID → QC-batch columns (QCBatch_ListView_Result{Validation|Approval}_Filter).
      // Both stages share the exact same column set — only the underlying Status differs.
      case 'result-approval':
      case 'result-validation-manual':
        return searchBy === 'JOBID' ? resultValidationJobCols() : resultValidationQcCols();
      case 'result-tracking':   return resultTrackingCols(searchBy);
    }
  }, [kind, searchBy, notify]);

  // Both Prep Queue and Analysis Queue drill down to a list of pending
  // sample-parameter rows for the chosen TestMethod (GoLive parity).
  const drillable = kind === 'prep-queue' || kind === 'analysis-queue';
  const getRowKey = (r: unknown): string | number => {
    if (kind === 'prep-queue' || kind === 'analysis-queue') return (r as AnalysisQueueRow).testMethodId;
    // Manual Result Validation QCBatchID mode = one row per (batch, job), so the
    // batch Oid alone isn't unique — key by batch + job (JOBID mode's id already
    // IS the job, so this stays unique there too).
    if (isManualDR) { const m = r as ResultValidationListRow; return `${m.id}::${m.jobOid ?? ''}`; }
    return (r as { id: string }).id;
  };

  // Export payload for the SDMS search-row Export icon (and reused by the toolbar).
  const gridEx = useMemo(() => gridExportMatrix(filtered, cols), [filtered, cols]);

  // ── QC Batches list — single-select + toolbar actions (Merged 2026-07-07) ───
  // Radio-like selection (one row at a time).  QC Batches drills to "open an
  // existing QC batch" (view mode) on row click — legacy row-click parity
  // (QCBatchController.cs :12079-12093 sets isview=true; the "Edit" toolbar
  // action is the only way to flip to edit mode).
  const handleQcSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setQcSel(next); return; }
    const added = [...next].find(id => !qcSel.has(id));
    setQcSel(added != null ? new Set([added]) : new Set());
  }, [qcSel]);

  // Analysis Queue single-select (radio-like) — drives "Create QC Batch". (Merged 2026-07-07.)
  const handleAnalysisSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setAnalysisSel(next); return; }
    const added = [...next].find(id => !analysisSel.has(id));
    setAnalysisSel(added != null ? new Set([added]) : new Set());
  }, [analysisSel]);

  // SDMS Result Validation / Approval — single-select (radio-like). The workflow is
  // "Step 1 · Select A QC Batch" (singular) → Open Package / Load / Rollback act on
  // that ONE batch (handleSdmsLoad opens selectedBatches[0]; Rollback sends a
  // one-element batchIds array; the stepper keys off length 0 vs >0). Ticking a
  // second row REPLACES the first so only one QC batch is ever selected — the list
  // no longer lets two batches be checked at once. (User request 2026-08-03.)
  const handleSdmsSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const added = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(added != null ? new Set([added]) : new Set());
  }, [selectedIds]);

  // Result Validation (manual) — single-select (radio-like); the Retrieve toolbar
  // button opens the ticked batch's detail (ALPACA: select → Retrieve → the
  // ResultEntry_Validation detail). A row-click opens it too.
  const handleManualSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const added = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(added != null ? new Set([added]) : new Set());
  }, [selectedIds]);
  const selectedManualRow = useMemo<ResultValidationListRow | null>(() => {
    if (!isManualDR || selectedIds.size !== 1) return null;
    const [id] = selectedIds;
    return (rows.find(r => {
      const m = r as ResultValidationListRow;
      return `${m.id}::${m.jobOid ?? ''}` === id;
    }) as ResultValidationListRow | undefined) ?? null;
  }, [isManualDR, selectedIds, rows]);

  // Open the Result Validation / Approval detail for a list row.  The Search By mode
  // decides the scope: JOBID → the whole job (all its QC batches); QCBatchID → ONE
  // job's samples within one batch + that batch's QC (per-(batch,job) row).  jobOid
  // is carried in BOTH modes so the detail + validate/rollback stay job-scoped.  The
  // `mode` prop switches the SAME detail component to the Approval stage.
  const openManualDetail = useCallback((r: ResultValidationListRow) => {
    setRvDetail({
      id: r.id, qcBatchID: r.qcBatchID, client: r.client, projectID: r.projectID,
      projectName: r.projectName, jobID: r.jobID, jobOid: r.jobOid,
      scope: searchBy === 'JOBID' ? 'job' : 'batch',
      mode: isManualAppr ? 'approval' : 'validation',
    });
  }, [searchBy, isManualAppr]);

  // Row-click SELECTS the row (ticks its checkbox → enables Load), matching Custom
  // Reporting's JobID-click flow (user request 2026-07-10) — it no longer navigates
  // straight to the detail.  The Load button is the ONLY way to open the detail (no
  // double-click).  Single-select (radio-like): clicking a row selects ONLY it;
  // clicking the already-ticked row clears it.  The manual checkbox + Load flow is unchanged.
  const onManualRowClick = useCallback((r: ResultValidationListRow) => {
    const key = `${r.id}::${r.jobOid ?? ''}`;
    setSelectedIds(prev =>
      (prev.size === 1 && prev.has(key)) ? new Set() : new Set<string | number>([key]));
  }, []);

  // ── Result Tracking — single-select checkbox + "Retrieve" drill-in ──────────
  // Legacy ResultEntryQueryPanel: tick ONE batch → Retrieve → the read-only
  // ResultView.  Row-click ticks the row (radio-like); Retrieve opens the detail.
  // Selection ids are the batch Oid (getRowKey returns r.id for this kind).
  const handleTrackingSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const added = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(added != null ? new Set([added]) : new Set());
  }, [selectedIds]);
  const onTrackingRowClick = useCallback((r: ResultTrackingRow) => {
    setSelectedIds(prev =>
      (prev.size === 1 && prev.has(r.id)) ? new Set() : new Set<string | number>([r.id]));
  }, []);
  const selectedTrackingRow = useMemo<ResultTrackingRow | null>(() => {
    if (!isTracking || selectedIds.size !== 1) return null;
    const [id] = selectedIds;
    return (rows.find(r => (r as ResultTrackingRow).id === id) as ResultTrackingRow | undefined) ?? null;
  }, [isTracking, selectedIds, rows]);
  const openTrackingDetail = useCallback((r: ResultTrackingRow) => {
    // JobID mode: r.id is a Samplecheckin.Oid → load the WHOLE job's results.
    setRtDetail({ id: r.id, qcBatchID: r.qcBatchID, client: r.client, projectID: r.projectID,
      jobID: r.jobID, isJob: searchBy === 'JOBID' });
  }, [searchBy]);
  // (Result Tracking rollback lives on the DETAIL only — see ResultTrackingDetail.
  //  The list keeps just Load.  User request 2026-07-25.)

  // All selected Prep Queue rows (multi-test). Selection ids are testMethodId.
  const selectedPrepRows = useMemo(() => {
    if (kind !== 'prep-queue') return [] as AnalysisQueueRow[];
    return (rows as AnalysisQueueRow[]).filter(r => analysisSel.has(r.testMethodId));
  }, [kind, analysisSel, rows]);

  const selectedAnalysisRow = useMemo(() => {
    if (kind !== 'analysis-queue' && kind !== 'prep-queue') return null;
    if (analysisSel.size < 1) return null;                    // >=1 (prep multi) → first row
    return (rows as AnalysisQueueRow[]).find(r => analysisSel.has(r.testMethodId)) ?? null;
  }, [kind, analysisSel, rows]);

  // Sample Prep "New" would be gated on the OG SamplePreparation WRITE permission
  // (OG SamplePreparationController :1571-1597 opens read-only without it). The
  // current nav-permission model has no write-view concept, so we fail OPEN (never
  // wrongly block) — the batch opens editable. Reinstate a write gate here if/when
  // per-view write permissions land.
  const canWritePrep = true;

  // New → open the QC Batch form in CREATE mode, seeded from the selected test
  // method. The form's own Job ID → Load → Sort → OK flow then builds the batch.
  const onNewFromQueue = () => {
    if (!selectedAnalysisRow) return;
    // Sample Prep Queue → open the Prep Batch detail (its own Job → Load → Sort →
    // OK flow); Analysis Queue → open the QC Batch detail.
    if (kind === 'prep-queue') {
      // OG parity (SamplePreparationController.NewObjectAction_Executing :1495):
      // multiple tests may be batched together ONLY when they are all Tier 1; any
      // Tier-2 method in a multi-select is blocked. In this DB every prep method is
      // Tier 2, so multi-select is effectively single-test until Tier-1 methods land.
      const sel = selectedPrepRows.length > 0 ? selectedPrepRows : [selectedAnalysisRow];
      if (sel.length > 1 && sel.some(r => Number(r.tierType ?? '') === 2)) {
        notify.warning('Only tier 1 will allow multiple tests.');
        return;
      }
      const first = sel[0];
      if (!first) return;
      const testMethodIds = [...new Set(sel.map(r => r.testMethodId))];
      const tierNum = Number((first.tierType ?? '').replace(/\D/g, '')) || 2;
      // OG: no SamplePreparation Write → the detail opens READ-ONLY (View mode).
      if (!canWritePrep) notify.warning('Read-only: you do not have write permission for Sample Preparation.');
      setPrepBatchSeed({
        testMethodId:  first.testMethodId,
        testMethodIds,
        tier:          tierNum,
        sort:          first.prepSort ?? 1,   // real PrepMethod Sort (was hardcoded 1)
        matrix:        first.matrix ?? undefined,
        test:          testMethodIds.length > 1
                         ? sel.map(r => r.test).filter(Boolean).join('; ')
                         : (first.test ?? undefined),
        method:        first.method ?? undefined,
        viewOnly:      !canWritePrep,
      });
      return;
    }
    setQcBatchSeed({
      testMethodId: selectedAnalysisRow.testMethodId,
      test:         selectedAnalysisRow.test,
      method:       selectedAnalysisRow.method,
      matrix:       selectedAnalysisRow.matrix,
      templateID:   selectedAnalysisRow.templateID,
    });
  };

  // Sample Details modal's OK-with-a-selection (legacy OG parity —
  // QCBatchController.cs Dc_Accepting ~line 2788 / Qcbatchinfo.lstSamples):
  // derive the DISTINCT job(s) + exact SampleLogIn oids from the checked rows
  // and seed QcBatchDetailPage with them directly, so Load narrows to exactly
  // those samples instead of pulling every pending sample for the test.
  const handleLoadSelectedSamples = (selectedRows: PrepDetailRow[]) => {
    if (!selectedQueue) return;
    const jobOids = [...new Set(selectedRows.map(r => r.jobOid).filter((v): v is string => !!v))];
    const sampleLogInOids = [...new Set(selectedRows.map(r => r.sampleLogInOid).filter((v): v is string => !!v))];
    setQcBatchSeed({
      testMethodId: selectedQueue.testMethodId,
      test:         selectedQueue.test,
      method:       selectedQueue.method,
      matrix:       selectedQueue.matrix,
      templateID:   selectedQueue.templateID,
      preSelectedJobOids:         jobOids,
      preSelectedSampleLogInOids: sampleLogInOids,
    });
  };

  // Prep Queue "View details" popup → OK: open the Prep Batch form seeded with the
  // ticked samples (their Job Oids pre-fill jobOids; their SampleLogIn Oids narrow
  // Load to exactly those). Mirrors the analysis-queue → QC flow above.
  const handleLoadSelectedPrepSamples = (selectedRows: PrepDetailRow[]) => {
    if (!selectedQueue) return;
    const jobOids = [...new Set(selectedRows.map(r => r.jobOid).filter((v): v is string => !!v))];
    const sampleLogInOids = [...new Set(selectedRows.map(r => r.sampleLogInOid).filter((v): v is string => !!v))];
    const tierNum = Number((selectedQueue.tierType ?? '').replace(/\D/g, '')) || 2;
    if (!canWritePrep) notify.warning('Read-only: you do not have write permission for Sample Preparation.');
    setSelectedQueue(null);
    setPrepBatchSeed({
      testMethodId:  selectedQueue.testMethodId,
      testMethodIds: [selectedQueue.testMethodId],
      tier:          tierNum,
      sort:          selectedQueue.prepSort ?? 1,
      matrix:        selectedQueue.matrix ?? undefined,
      test:          selectedQueue.test ?? undefined,
      method:        selectedQueue.method ?? undefined,
      viewOnly:      !canWritePrep,
      preSelectedJobOids:         jobOids,
      preSelectedSampleLogInOids: sampleLogInOids,
    });
  };

  const selectedQcRow = useMemo(() => {
    if (kind !== 'qc-batches' || qcSel.size !== 1) return null;
    const [id] = qcSel;
    return (rows as AnalyticalBatchRow[]).find(r => r.id === id) ?? null;
  }, [kind, qcSel, rows]);

  // Sample Prep Batches list — single-select (reuses qcSel; the kinds are mutually
  // exclusive) driving Edit (open the batch) + Delete (OG SamplePrepBatch_ListView:
  // Delete blocked once ResultCompleted=3). New is a QUEUE action, not here.
  const selectedPrepBatchRow = useMemo(() => {
    if (kind !== 'prep-batches' || qcSel.size !== 1) return null;
    const [id] = qcSel;
    return (rows as PrepBatchRow[]).find(r => r.id === id) ?? null;
  }, [kind, qcSel, rows]);
  const prepDeletable = (r: PrepBatchRow | null) => !!r && r.statusCode !== 3;
  const onPrepBatchEdit = () => {
    const row = selectedPrepBatchRow;
    if (!row) return;
    setPrepBatchSeed({
      testMethodId: '', tier: row.tier ?? 2, sort: row.sort ?? 1,
      matrix: row.matrix ?? undefined, test: row.test ?? undefined, method: row.method ?? undefined,
      prepOid: row.id,
    });
  };
  // Row click → open the existing prep batch in VIEW mode (QC-form parity,
  // onQcBatchRowClick). Same seed as Edit but viewOnly:true so the detail opens
  // read-only regardless of status; Edit remains the editable path.
  const onPrepBatchRowClick = (r: unknown) => {
    const row = r as PrepBatchRow;
    setPrepBatchSeed({
      testMethodId: '', tier: row.tier ?? 2, sort: row.sort ?? 1,
      matrix: row.matrix ?? undefined, test: row.test ?? undefined, method: row.method ?? undefined,
      prepOid: row.id, viewOnly: true,
    });
  };
  const onPrepBatchDelete = () => {
    const row = selectedPrepBatchRow;
    if (!prepDeletable(row) || !row) return;
    void runDelete({
      formName: 'Sample Prep Batches',
      message: `Delete Prep Batch ${row.batchId ?? ''}? Its samples return to the prep queue and this cannot be undone.`,
      delete: () => prepBatchApi.deleteBatch(String(row.id)).then(() => undefined),
      onDeleted: () => { setQcSel(new Set()); void load(); },
      onError: (e) => notify.error(qcApiErr(e, 'Deleting the prep batch failed.')),
    });
  };

  // Status gates mirror the legacy list-view action criteria: Edit/Delete are
  // enabled for NonStatus In(0,1) (persistent Status 0 = a fresh AI batch, 1 =
  // Pending Submission), Rollback for NOT In(0,1) (Pending Validation onward).
  const qcEditable     = (r: AnalyticalBatchRow | null) => !!r && (r.statusCode === 0 || r.statusCode === 1);
  const qcRollbackable = (r: AnalyticalBatchRow | null) => !!r && !(r.statusCode === 0 || r.statusCode === 1);

  // Row click → open the existing QC batch in VIEW mode (legacy isview=true).
  const onQcBatchRowClick = (r: unknown) => {
    const row = r as AnalyticalBatchRow;
    // testMethodId/test/method/matrix/templateID aren't fully known yet — the
    // AnalyticalBatchRow list DTO carries only the display Test/Method/Matrix
    // NAME strings; QcBatchDetailPage's detail fetch (keyed on existingAbOid)
    // resolves the real values and overwrites these once it loads.
    setQcBatchSeed({
      testMethodId: '', test: row.test, method: row.method, matrix: row.matrix,
      templateID: row.templateID, existingAbOid: row.id, viewOnly: true,
    });
  };

  // New → jump to the Analysis Queue (where a QC batch is started by ticking a test).
  const onQcNew = () => onOpenNav?.('analysisq');

  // Edit → open the selected batch in EDIT mode (legacy EditAction — status-gated).
  const onQcEdit = () => {
    const row = selectedQcRow;
    if (!qcEditable(row) || !row) return;
    setQcBatchSeed({
      testMethodId: '', test: row.test, method: row.method, matrix: row.matrix,
      templateID: row.templateID, existingAbOid: row.id, viewOnly: false,
    });
  };

  // Delete → shared delete flow → the cascade endpoint (samples return to the
  // queue); status-gated + server guards. Refresh the list on success.
  const onQcDelete = () => {
    const row = selectedQcRow;
    if (!qcEditable(row) || !row) return;
    void runDelete({
      formName: 'QC Batches',
      message: `Delete QC Batch ${row.batchId ?? ''}? Its samples return to the analysis queue and this cannot be undone.`,
      delete: () => qcBatchApi.deleteBatch(row.id).then(() => undefined),
      onDeleted: () => { setQcSel(new Set()); void load(); },
      onError: (e) => notify.error(qcApiErr(e, 'Deleting the QC batch failed.')),
    });
  };

  // Rollback → reason prompt (legacy "Rollbackempty" block) → rollback endpoint;
  // enabled only past Pending Submission (NonStatus Not In(0,1)). Refresh on success.
  const onQcRollback = async () => {
    const row = selectedQcRow;
    if (!qcRollbackable(row) || !row) return;
    const reason = await prompt({
      title: 'QC Batch Rollback', message: 'Enter a reason for the rollback.',
      placeholder: 'Rollback reason…', required: true, confirmLabel: 'Rollback',
    });
    if (reason === null) return;
    try {
      await qcBatchApi.rollback(row.id, reason);
      void showDialog({ type: 'success', title: 'Rollback', message: `QC Batch ${row.batchId ?? ''} rolled back.` });
      setQcSel(new Set());
      void load();
    } catch (e) { notify.error(qcApiErr(e, 'Rollback failed.')); }
  };

  // "Result Validation" (manual) — Validate / Rollback live ONLY in the DETAIL
  // view (ResultValidationDetail), matching ALPACA: the LIST just browses and
  // OPENS a batch (row-click → detail); the batch is validated / rolled back from
  // its detail's toolbar.  So the list carries no Validate/Rollback and no
  // multi-select checkboxes.

  // ── SDMS workflow handlers (Validate / Rollback / Approve applied optimistically) ───
  // (Merged 2026-07-07) After the POST succeeds, mutate the matching rows in place
  // so the grid reflects the new state immediately.  A Refresh re-pulls
  // authoritative server state once the Phase 2 SP writes are in place.
  const applyValidate = (batchIds: string[], whoCode: string, whenIso: string) => {
    const idSet = new Set(batchIds);
    setRows(prev => prev.map(r => {
      const b = r as AnalyticalBatchRow;
      if (!idSet.has(b.id)) return r;
      return { ...b,
        statusText:       'SDMS Validated',
        statusId:         'DV-1-2',
        validatedByList:  whoCode,
        validatedDateMax: whenIso,
      };
    }));
    setSelectedIds(new Set());
    // Success confirmation is shown by ValidateModal's phase-2 popup (centred
    // dialog + green tick + OK button — ALPACA-parity).  No toast here to
    // avoid the "modal + toast for the same event" double-notify.
  };
  const applyRollback = (batchIds: string[]) => {
    const idSet = new Set(batchIds);
    // Full ALPACA rollback sends the batch all the way back to result entry
    // (Status 1 / QB-1-3), so it LEAVES the SDMS Result Validation list. Drop
    // the rolled-back rows optimistically; a Refresh re-pulls authoritative state.
    setRows(prev => prev.filter(r => !idSet.has((r as AnalyticalBatchRow).id)));
    setSelectedIds(new Set());
  };
  const applyApprove = (batchIds: string[], whoCode: string, whenIso: string) => {
    const idSet = new Set(batchIds);
    // An approved batch STAYS in the SDMS Result Approval grid, shown as
    // "SDMS Approved" with its verify stamp (ALPACA parity — the Level-3 grid
    // lists both validated and approved).  Update in place; a Refresh re-pulls
    // authoritative state (with the approver resolved to an E-code).
    setRows(prev => prev.map(r => {
      const b = r as AnalyticalBatchRow;
      if (!idSet.has(b.id)) return r;
      return { ...b,
        statusText:     'SDMS Approved',
        statusId:       'DV-6-4',
        verifiedByName: whoCode,
        verifiedDate:   whenIso,
      };
    }));
    setSelectedIds(new Set());
  };

  // Selected rows (typed) + Validate/Rollback enablement (Pending vs Validated).
  const selectedBatches = useMemo<AnalyticalBatchRow[]>(() => {
    if (!isSdms) return [];
    return (rows as AnalyticalBatchRow[]).filter(r => selectedIds.has(r.id));
  }, [rows, selectedIds, isSdms]);
  // Rollback-able = ANY selected SDMS batch on this list — Pending SDMS
  // Validation (Status 2) AND SDMS Validated / Approved (Status 5). LIMS parity
  // (DataReviewBatchRollbackAction_Execute): rollback rolls BOTH all the way back
  // to Result Entry (QB-1-3); it imposes no "must be validated first" gate — only
  // a Reported sample blocks it, which the server precheck enforces. (Previously
  // Pending SDMS Validation was excluded, greying out Rollback for those rows.)
  const rollbackTargets = selectedBatches;

  // Top-bar stepper progress (reference: Field-Data-Entry-Prototype.html — each
  // step ticks green as it is completed).  On the LIST view the ONLY step the
  // analyst performs is "Select a QB"; the remaining steps (Batch Results …
  // Validate/Approve) happen after Open Package, inside the detail overlay (which
  // drives its own stepper).  So the list bar shows exactly one transition:
  // nothing selected → step 1 active; ≥1 selected → step 1 done + step 2 active.
  const sdmsStepStates = useMemo<Array<'done' | 'active' | 'todo'>>(() => {
    // Package detail open: the QB is selected + loaded, and the analyst is now
    // reviewing (Batch Results / Check Points / Review Log) with Validate/Approve
    // pending — so the pill advances past Load. (User request 2026-07-07.)
    if (pkgTarget) return ['done', 'done', 'active', 'active', 'active', 'todo'];
    return selectedBatches.length === 0
      ? ['active', 'todo', 'todo', 'todo', 'todo', 'todo']
      : ['done', 'active', 'todo', 'todo', 'todo', 'todo'];
  }, [selectedBatches.length, pkgTarget]);

  // SDMS title-row actions (prototype (7)): Rollback + Load moved OUT of the
  // toolbar into the consolidated title row.  Load opens the ticked batch's
  // package (same as the per-row "Open Package" link).
  const handleSdmsRollback = async () => {
    // ALPACA parity: the reported-sample guard fires BEFORE the reason popup.
    // Pre-check the selected batches; if any is blocked, show the red "Rollback
    // failed…" notice and DON'T ask for a reason.
    const ids = rollbackTargets.map(t => t.id);
    if (ids.length === 0) return;
    try {
      const res = await api.post('/api/v1/analysis/result-validation/rollback/precheck', { batchIds: ids });
      const blocked = (res.data as { blocked?: Record<string, string> }).blocked ?? {};
      const blockedCount = Object.keys(blocked).length;
      if (blockedCount > 0) {
        const firstReason = Object.values(blocked)[0] ?? 'Rollback failed.';
        // §13: a full refusal is an error (red modal); a partial skip is a
        // warning (amber modal). Both via the shared global service.
        if (blockedCount === ids.length) notify.error(firstReason);
        else notify.warning(`${blockedCount} batch(es) skipped: ${firstReason}`);
        return;   // guard failed → no reason dialog (LIMS behaviour)
      }
      setRollbackOpen(true);   // all eligible → ask for the reason
    } catch {
      // Pre-check failed (network/API) — fall back to the modal; the server
      // re-checks the same gate on submit anyway.
      setRollbackOpen(true);
    }
  };
  const handleSdmsLoad = () => {
    if (selectedBatches.length > 0) setPkgTarget(selectedBatches[0] as AnalyticalBatchRow);
  };

  // (Merged 2026-07-07) Full-page swap: clicking New (→ Analysis Queue is handled
  // by onOpenNav) or a QC Batch row Edit/row-click opens the inline detail form.
  // Sample Prep Queue → New (or a Prep Batches row) swaps the list for the inline
  // Prep Batch detail form (its own Job → Load → Sort → OK flow).
  if (prepBatchSeed) {
    return (
      <PrepBatchDetailPage
        seed={prepBatchSeed}
        // Clear the queue row selection on return so the row isn't left ticked
        // after New (Prep/Analysis Queue selection lives in analysisSel). (fix 2026-08-14)
        onBack={() => { setPrepBatchSeed(null); setAnalysisSel(new Set()); void load(); }}
        onExportToQc={(ex) => {
          // OG btnExportToQCBatch_Execute: swap the prep detail for the QC Batch
          // form, seeded with this prep batch so it auto-imports the samples.
          setPrepBatchSeed(null);
          setQcBatchSeed({
            testMethodId: ex.testMethodId,
            test:         ex.test,
            method:       ex.method,
            matrix:       ex.matrix,
            templateID:   null,
            preSelectedPrepBatchId: ex.prepBatchId,
          });
        }}
      />
    );
  }

  if (qcBatchSeed) {
    return (
      <QcBatchDetailPage
        seed={{
          testMethodId: qcBatchSeed.testMethodId,
          test:         qcBatchSeed.test,
          method:       qcBatchSeed.method,
          matrix:       qcBatchSeed.matrix,
          templateID:   qcBatchSeed.templateID,
          preSelectedJobOids:         qcBatchSeed.preSelectedJobOids,
          preSelectedSampleLogInOids: qcBatchSeed.preSelectedSampleLogInOids,
          existingAbOid: qcBatchSeed.existingAbOid,
          viewOnly:      qcBatchSeed.viewOnly,
          autoOpenSdms:  qcBatchSeed.autoOpenSdms,
        }}
        // Clear the Analysis Queue row selection on return so the row isn't left
        // ticked after New (analysis-queue selection lives in analysisSel). (fix 2026-08-14)
        onBack={() => { setQcBatchSeed(null); setAnalysisSel(new Set()); void load(); }}
      />
    );
  }

  // Result Validation → detail drill-in (full-page swap, like the QC Batch detail).
  if (rvDetail) {
    return (
      <ResultValidationDetail
        batch={rvDetail}
        canWrite={perm.write}
        onClose={() => setRvDetail(null)}
        onDone={() => { setRvDetail(null); void load(); }}
      />
    );
  }

  // Result Tracking → read-only "Retrieve" drill-in (full-page swap).  Purely a
  // viewer — closing returns to the list (no reload needed; nothing changed).
  if (rtDetail) {
    return <ResultTrackingDetail batch={rtDetail} onClose={() => setRtDetail(null)} />;
  }

  return (
    <div className={`bl-page${isSdms ? ' sdms-compact' : ''}`}>
      {/* Result Validation workflow stepper (BO UI Standard §5) — docked in the
          top bar. On the LIST, step 1 (Select Job) is active; the detail view
          drives steps 2-4 (Sample Results · QC Sample Results · Validate). */}
      {isManualDR && (
        <TopbarStepper steps={[
          { id: 'select',   label: 'Select Job',        state: 'active' },
          { id: 'sample',   label: 'Sample Results',    state: 'todo' },
          { id: 'qc',       label: 'QC Sample Results',  state: 'todo' },
          { id: 'action',   label: isManualAppr ? 'Approve' : 'Validate', state: 'todo' },
        ] as IslandStep[]} />
      )}
      {/* Result Tracking Dynamic-Island stepper (BO UI Standard §5, prototype
          Result-Entry): LIST = step 1 (Select Batch) active; the detail view marks
          both steps done. */}
      {isTracking && (
        <TopbarStepper steps={[
          { id: 'select', label: 'Select Batch', state: 'active' },
          { id: 'load',   label: 'Load Samples', state: 'todo' },
        ] as IslandStep[]} />
      )}
      {/* Rollback outcomes surface via the shared §13 notifications (useNotify):
          success → transient top banner, refusal → centered modal. The old
          per-form red/green banners were removed (standing rule 7). */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>{group}</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{title}</span>
          </div>
          {/* Non-SDMS keeps the ONE shared <FormTitle> (nav icon/colour via
              navMeta(KIND_NAV_ID[kind]) + the TOP load-time chip, §18.6).
              SDMS renders its own consolidated title row (prototype (7) full
              match): check + title + load-time chip + Audit Trail on the left;
              Rollback + Load on the right. (Merged 2026-07-07.) */}
          <div className="dp-title-row">
            {isSdms ? (
              <>
                <div className="dp-title-lead">
                  <h1 className="dp-title">
                    <i className="ti ti-circle-check dp-title-check" aria-hidden="true" />{title}
                  </h1>
                  <LoadTimeChip ms={loadMs} loading={loading} />
                  <AuditTrailButton formName={isApproval ? 'Result Approval' : 'SDMS Result Validation'} />
                </div>
                <div className="dp-title-actions">
                  <button className="ca-btn" onClick={handleSdmsRollback}
                    disabled={rollbackTargets.length === 0 || !perm.write}
                    title={
                      !perm.write
                        ? "Read-only access — contact your administrator to request Write permission"
                        : "Rollback the selected batch to Pending Submission (Result Entry)"
                    }>
                    <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-blue)' }} /> Rollback
                  </button>
                  <button className="ca-btn ca-btn-solid dp-load" onClick={handleSdmsLoad}
                    disabled={selectedBatches.length === 0}
                    title="Open the ticked QC batch's package for review">
                    <i className="ti ti-download" /> Load
                  </button>
                </div>
              </>
            ) : isManualDR ? (
              <>
                {/* Audit — the shared config-gated <AuditTrailButton> chip next to
                    the load-time chip (standing rule 11, prototype parity). */}
                <FormTitle pageId={KIND_NAV_ID[kind]} title={title} loadMs={loadMs} loading={loading}
                  auditFormName={isManualAppr ? 'Result Approval' : 'Result Validation'} />
                {/* Load — TOP-RIGHT primary action (Custom Reporting parity): DISABLED
                    until exactly one QC batch / job is ticked, then opens it in the
                    Result Validation / Approval detail. */}
                <div className="dp-title-actions">
                  <button className="ca-btn ca-btn-solid dp-load"
                    onClick={() => { if (selectedManualRow) openManualDetail(selectedManualRow); }}
                    disabled={!selectedManualRow}
                    title={selectedManualRow
                      ? `Load the ticked ${searchBy === 'JOBID' ? 'job' : 'QC batch'} into ${title}`
                      : `Tick a ${searchBy === 'JOBID' ? 'Job ID' : 'QC batch'} to enable Load.`}>
                    <i className="ti ti-download" /> Load{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                  </button>
                </div>
              </>
            ) : isTracking ? (
              <>
                {/* Audit — shared config-gated <AuditTrailButton> chip next to the
                    load-time chip (standing rule 11, prototype parity).  Surfaces the
                    Rollback audit rows (FormName "Result Tracking"). */}
                <FormTitle pageId={KIND_NAV_ID[kind]} title={title} loadMs={loadMs} loading={loading}
                  auditFormName="Result Tracking" />
                {/* Load — TOP-RIGHT primary action (Custom Reporting / Result
                    Validation parity, §18.6 standard prototype): DISABLED until one
                    QC batch is ticked, then opens its read-only result rows. */}
                <div className="dp-title-actions">
                  <button className="ca-btn ca-btn-solid dp-load"
                    onClick={() => { if (selectedTrackingRow) openTrackingDetail(selectedTrackingRow); }}
                    disabled={!selectedTrackingRow}
                    title={selectedTrackingRow
                      ? "Load the ticked QC batch's result rows"
                      : 'Tick a QC batch to enable Load.'}>
                    <i className="ti ti-download" /> Load{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                  </button>
                  {/* Rollback lives ONLY on the detail (row-level), per user request
                      2026-07-25 — not on the list.  ResultTrackingDetail owns it. */}
                </div>
              </>
            ) : (
              <FormTitle pageId={KIND_NAV_ID[kind]} title={title} loadMs={loadMs} loading={loading} />
            )}
          </div>
          <div className="dp-subtitle">
            {/* SDMS: single clean sentence (prototype (7)) — the item count lives
                in the grid footer and selection is shown by the stepper + hint, so
                no "· N items / · N selected" clutter here. */}
            {isSdms
              ? (isApproval
                  ? 'Analytical data packages awaiting level-3 (SDMS) approval.'
                  : 'Analytical data packages awaiting level-2 (SDMS) validation.')
              : isManualDR
                /* Result Validation / Approval subtitle (Data-Review prototype). */
                ? (isManualAppr
                    ? <>Result-validated jobs awaiting final <b>Result Approval</b> (DV-10-3). Approval releases the job to Reporting. Tick a Job ID / QC batch and click <b>Load</b>.</>
                    : <>Jobs whose QC batches are all <b>SDMS Approved</b> (DV-1-3), now awaiting job-level <b>Result Validation</b> (DV-10-2). Tick a Job ID / QC batch and click <b>Load</b>.</>)
                : isTracking
                  /* Result Tracking subtitle — prototype (Result-Entry) content, with
                     AI-accurate terms (Load, Pending Entry). */
                  ? <>Every batch that has been through result entry — who entered / submitted / rolled back, and when. <b>Load</b> opens the full record; <b>Rollback</b> (mandatory reason, audited) returns its results to <b>Pending Entry</b>.</>
                  : <>{'Read-only'}{' · '}{loading ? '…' : rows.length} items{subtitle ? ' · ' : ''}{subtitle}</>}
          </div>
        </div>
        {/* Orange step-hint strip (prototype (7)) — INSIDE .bl-top so it shares
            the title's 20px horizontal inset.  One per screen: the island owns
            the step list, this owns the guidance sentence. (Merged 2026-07-07.) */}
        {isSdms && !pkgTarget && (
          <div className="dv-instr">
            <b>Step 1 · Select a QC Batch.</b>{' '}
            {isApproval
              ? (selectedBatches.length > 0
                  ? <>QC Batch selected — click <b>Open Package</b> to review &amp; approve.</>
                  : <>Tick a validated batch, then click <b>Open Package</b> to review its results before approving.</>)
              : (selectedBatches.length > 0
                  ? <>QC Batch selected — click <b>Open Package</b> to review &amp; validate.</>
                  : <>Tick a pending batch, then click <b>Open Package</b> to review its results before validating.</>)}
          </div>
        )}
      </div>

      {/* Dynamic Island stepper — docked into the TOP APP BANNER via a portal
          (Class C/D standard): topmost + always visible.  Renders into AppShell's
          #ai-topbar-island-slot; unmounts (and clears the slot) on navigate-away.
          The step list is NOT duplicated as caption text — this pill is the sole
          stepper. (Merged 2026-07-07.) */}
      {/* Show on the list AND when the package detail is open (user request
          2026-07-07) — the detail now renders inside the content area with the
          top bar kept, so the stepper stays visible instead of vanishing. */}
      {isSdms && bannerSlot && createPortal(
        <div className="dv-flow dv-island-top">
          {['Select a QB', 'Load', 'Batch Results', 'Check Points', 'Review Log', isApproval ? 'Approve' : 'Validate']
            .map((label, i, arr) => (
            <span key={label} className="dv-fseg">
              <span className={`dv-fstep dv-${sdmsStepStates[i]}`}>
                <span className="dv-fn">{sdmsStepStates[i] === 'done' ? '✓' : i + 1}</span>{label}
              </span>
              {i < arr.length - 1 && (
                <span className={`dv-farrow${sdmsStepStates[i] === 'done' ? ' dv-done' : ''}`} />
              )}
            </span>
          ))}
        </div>,
        bannerSlot,
      )}

      {/* Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
          only the grid card below is white. */}
      <div className="bl-grid-area">
        {/* Contextual guidance strip (BO UI Standard §5, shared <StepHint>) —
            narrates the ACTIVE step only ("Step N · Name." + one imperative, bold
            controls) and updates live as the stepper advances; ONE per workflow
            screen, under the title + above the toolbar. Here = Step 1 (Select Job);
            the detail view carries Steps 2-3. */}
        {isManualDR && (isManualAppr ? (
          // Result Approval — the segregation-of-duties reminder (ALPACA
          // ResultApproval_Execute: approver must differ from the analyst).  Uses the
          // SAME orange `guidance` tone as the Result Validation strip (user request
          // 2026-07-10: match Result Validation's colour).  The green subtitle above
          // already carries the "tick + Load" instruction.
          // WORDING (2026-08-25): says ANALYSIS, not "validation". The gate this reminds the
          // user about compares the ANALYST against the approver — a validator approving what
          // they validated is allowed, in ALPACA and here (see AnalysisWriter's approve gate).
          // "Validation and approval ... different users" named the one pair NEITHER system
          // checks, and sent a client looking at ValidatedBy when an approve was refused.
          // ALPACA's own ERROR DIALOG keeps that legacy wording verbatim for parity — this
          // banner has no ALPACA counterpart (ALPACA shows only a transient message when the
          // gate fires), so correcting it costs no parity. Owner decision: fix the banners,
          // leave the dialog.
          <StepHint tone="guidance">
            <i className="ti ti-info-circle" /> Analysis and approval of analysis
            results must be performed by <b>different users</b>.
          </StepHint>
        ) : (
          <StepHint tone="guidance">
            <b>Review · Validation.</b> Tick a Job ID / QC batch, then <b>Load</b> to review every
            Sample Result &amp; QC Sample Result, then <b>Validate</b> — which advances the job to
            <b> Pending Result Approval (DV-10-3)</b>.
          </StepHint>
        ))}
        {/* Result Tracking guidance strip — SAME shared <StepHint tone="guidance">
            style as Result Approval; content from the prototype (Result-Entry).
            (Rollback wording omitted — Result Tracking is read-only in AI.) */}
        {/* Result Tracking guidance strip — prototype content with the "Tracking. Date
            Filter defaults… Set Search By" lead-in removed (user request 2026-07-25);
            the rest is unchanged. */}
        {isTracking && (
          <StepHint tone="guidance">
            <i className="ti ti-info-circle" /> Tick a Job ID / QC batch, then <b>Load</b> to view the
            record — or open it and <b>Rollback</b> to send its results back for re-entry.
          </StepHint>
        )}
        {/* Toolbar on its OWN full-width row on the grey page. */}
        <div style={{ margin: '0 0 8px' }}>
          <AnalysisToolbar
            kind={kind}
            loading={loading}
            dateRange={dateRange} setDateRange={setDateRange}
            searchBy={searchBy} setSearchBy={setSearchBy}
            onRetrieve={load}
            exportData={{ filename: kind, ...gridEx }}
            // SDMS Result Validation / Approval workflow actions. Rollback moved
            // to the consolidated title row (prototype (7)); Preview stays here.
            // Validate/Approve is only reachable via Open Package (SdmsPackageDetail).
            sdmsCanPreview={selectedBatches.length === 1}
            onSdmsPreview={() => {
              const only = selectedBatches.length === 1 ? selectedBatches[0] : undefined;
              if (only) setPreviewTarget(only);
            }}
            // (Merged 2026-07-07) QC Batches list-view actions — New (→ Analysis
            // Queue), Edit / Delete (status In(0,1)), Rollback (Not In(0,1)), SDMS.
            onQcNew={onQcNew}
            onQcEdit={onQcEdit}
            qcEditable={qcEditable(selectedQcRow)}
            onQcDelete={onQcDelete}
            onQcRollback={() => void onQcRollback()}
            qcRollbackable={qcRollbackable(selectedQcRow)}
            onQcSdms={() => {
              const row = selectedQcRow;
              if (!row) return;
              // Open the selected batch's detail (fetches keyed on its Oid) and
              // auto-swap into the SDMS sheet — the same sheet the detail-view
              // SDMS button opens. Read-only load; Back returns to this list.
              setQcBatchSeed({
                testMethodId: '', test: row.test, method: row.method, matrix: row.matrix,
                templateID: row.templateID, existingAbOid: row.id, viewOnly: true,
                autoOpenSdms: true,
              });
            }}
            qcHasSelection={!!selectedQcRow}
            // (Merged 2026-07-07) Analysis Queue → Create QC Batch.
            onQueueNew={onNewFromQueue}
            queueHasSelection={!!selectedAnalysisRow}
            // Sample Prep Batches list — Edit (open) / Delete (blocked once ResultCompleted).
            onPrepBatchEdit={onPrepBatchEdit}
            onPrepBatchDelete={onPrepBatchDelete}
            prepBatchHasSelection={!!selectedPrepBatchRow}
            prepBatchDeletable={prepDeletable(selectedPrepBatchRow)}
          />
        </div>

        {/* Search — shared .list-search-row on the grey page, never inside the card. */}
        <div className="list-search-row">
          <div className="list-search">
            <i className="ti ti-search" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search…" />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          {filtered.length !== rows.length && (
            <span className="search-match-count">
              {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
            </span>
          )}
          {/* SDMS list — Export + Maximize icons on the search row (search left, icons
              right), matching the detail-view tab grids (user request 2026-08-31). */}
          {isSdms && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <ExportMenu filename={kind} headers={gridEx.headers} rows={gridEx.rows}
                iconOnly triggerClassName="adg-tool" />
              <button type="button"
                className={`adg-tool${gridMaxed ? ' adg-tool-on' : ''}`}
                title={gridMaxed ? 'Restore' : 'Maximize'} aria-label={gridMaxed ? 'Restore' : 'Maximize'}
                aria-pressed={gridMaxed} onClick={() => setGridMaxed((m) => !m)}
                /* Float above the maximized grid (z > .adg-maxed), at the content's
                   top-right (below the top bar), so Restore stays clickable. */
                style={gridMaxed ? { position: 'fixed', top: 50, right: 24, zIndex: 1401 } : undefined}>
                <i className={`ti ${gridMaxed ? 'ti-arrows-minimize' : 'ti-arrows-maximize'}`} />
              </button>
            </span>
          )}
        </div>

        {/* ONE white grid card — filter-chip strip + grid + its single footer. */}
        <div className="bl-card bl-card-flex">
          {/* Status filter chips — only when the dataset carries a status column.
              Hidden on SDMS Result Validation / Approval (not in legacy ALPACA's list). */}
          {!isSdms && statusChips.length > 0 && (
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">Status:</span>
              <button
                className={`sa-filter-btn${statusChip === 'all' ? ' active' : ''}`}
                onClick={() => setStatusChip('all')}
              >All ({searchFiltered.length})</button>
              {statusChips.map(([label, n]) => (
                <button key={label}
                  className={`sa-filter-btn${statusChip === label ? ' active' : ''}`}
                  onClick={() => setStatusChip(label)}
                >{label} ({n})</button>
              ))}
            </div>
          )}

          {error ? (
            <div className="bl-empty" style={{ color: 'var(--ai-red)', flexDirection: 'column', gap: 6 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 24, opacity: .35 }} />
              <div>{error}</div>
            </div>
          ) : (
            /* Shared data grid — styling, per-column filters, sort, footer /
               pagination and load-time are all standardised in <AppDataGrid>.
               SDMS Result Validation / Approval enable row selection so the
               Rollback / Preview / Load actions can act on the selected batches. */
            <AppDataGrid<unknown>
              rows={filtered}
              columns={cols}
              // Persist this list's column layout + sort per screen (kind), so the
              // user's Hide/Reorder/Resize/Sort choices restore on return (§18.6).
              persistKey={`analysis-${kind}`}
              getRowId={getRowKey}
              loading={loading}
              loadMs={loadMs}
              // QC Batches row-click opens the existing batch inline (view mode);
              // Prep/Analysis Queue rows drill to the sample slide-over. (Merged 2026-07-07.)
              // Result Validation (manual): a row-click SELECTS the row (ticks its box →
              // enables Load) like Custom Reporting — it does NOT navigate. The Load
              // button is the ONLY way to open the detail (no double-click). (User 2026-07-10.)
              // Sample Prep Batches row-click opens the existing prep batch inline
              // (view mode). Prep/Analysis Queue rows still drill to the sample panel.
              onRowClick={kind === 'qc-batches' ? onQcBatchRowClick
                : kind === 'prep-batches' ? onPrepBatchRowClick
                : isManualDR ? (r => onManualRowClick(r as ResultValidationListRow))
                : isTracking ? (r => onTrackingRowClick(r as ResultTrackingRow))
                : drillable ? (r => setSelectedQueue(r as AnalysisQueueRow)) : undefined}
              // SDMS Result Validation / Approval enable single-select (radio-like) —
              // the workflow selects ONE QC batch at a time (user request 2026-08-03).
              // QC Batches / Analysis + Prep Queue / Prep Batches / Result Validation +
              // Approval (manual) / Result Tracking are all single-select too. Prep
              // Queue's checkbox drives the "New" prep-batch action (row-click still
              // drills to the sample panel); Prep Batches' checkbox drives Edit/Delete
              // (reusing qcSel — the kinds are mutually exclusive).
              selectable={isSdms || isManualDR || isTracking || kind === 'qc-batches' || kind === 'analysis-queue' || kind === 'prep-queue' || kind === 'prep-batches'}
              selectedIds={isSdms || isManualDR ? selectedIds : kind === 'qc-batches' || kind === 'prep-batches' ? qcSel : kind === 'analysis-queue' || kind === 'prep-queue' ? analysisSel : isTracking ? selectedIds : undefined}
              onSelectionChange={isSdms ? handleSdmsSelectionChange : isManualDR ? handleManualSelectionChange : kind === 'qc-batches' || kind === 'prep-batches' ? handleQcSelectionChange : kind === 'analysis-queue' || kind === 'prep-queue' ? handleAnalysisSelectionChange : isTracking ? handleTrackingSelectionChange : undefined}
              // QC Batches default sort = QCBatchID descending (legacy list-view
              // SortIndex 0 / SortOrder Descending) — newest batch first. (Merged 2026-07-07.)
              initialSort={kind === 'qc-batches' ? { key: 'QCBatchID', dir: 'desc' } : undefined}
              // SDMS Result Validation / Approval — full-screen via className, toggled by
              // the Maximize icon in the search row (user request 2026-08-31).
              className={isSdms && gridMaxed ? 'adg-maxed' : undefined}
              emptyText="No rows match the current filters."
            />
          )}
        </div>
      </div>

      {selectedQueue && (
        // Analysis Queue has its OWN readiness criteria (AI_Analysis_AnalysisQueueDetail_SP),
        // so it drills in through SampleDetailsModal — a sample PICKER whose OK seeds the
        // QC Batch with exactly the checked samples. Prep Queue keeps the read-only panel.
        kind === 'analysis-queue'
          ? <SampleDetailsModal row={selectedQueue} onClose={() => setSelectedQueue(null)} onLoadSelected={handleLoadSelectedSamples} />
          : <PrepQueueDetailPanel row={selectedQueue} onClose={() => setSelectedQueue(null)} onLoadSelected={handleLoadSelectedPrepSamples} />
      )}

      {/* SDMS Result Validation / Approval modals — Validate / Approve is opened
          from <SdmsPackageDetail>'s in-view toolbar, not from here. (Merged 2026-07-07.) */}
      {rollbackOpen && rollbackTargets.length > 0 && (
        <RollbackModal
          targets={rollbackTargets as unknown as SdmsBatch[]}
          onClose={() => setRollbackOpen(false)}
          onConfirmed={(resp) => {
            // Only apply optimistic rollback to the batches the server actually
            // rolled back (server may skip some — e.g. reported ones — while
            // succeeding on others in a bulk request).
            const skipped = resp.skipped ?? {};
            const skippedIds = new Set(Object.keys(skipped));
            const succeededIds = rollbackTargets
              .map(t => t.id)
              .filter(id => !skippedIds.has(id));
            if (succeededIds.length > 0) {
              applyRollback(succeededIds);
            }
            // Surface the FIRST skip reason as an ALPACA-style red banner across
            // the top of the list.  All-skip: use the single message.  Mixed:
            // "N batch(es) skipped: {reason}" so partial success is truthful.
            const skipCount = Object.keys(skipped).length;
            if (skipCount > 0) {
              const firstReason = Object.values(skipped)[0] ?? 'Rollback failed.';
              // §13: full skip → error modal; partial → warning modal.
              if (skipCount === rollbackTargets.length) notify.error(firstReason);
              else notify.warning(`${skipCount} batch(es) skipped: ${firstReason}`);
            } else if (succeededIds.length > 0) {
              // Milestone success (§18.6) → the shared CENTERED modal, not the
              // Tier-2 top banner: rolling a validated batch back is a significant
              // state change the reviewer should acknowledge (client 2026-08-03).
              void showDialog({ type: 'success', title: 'Rollback', message: 'Rollbacked successfully.' });
            }
            setRollbackOpen(false);
          }}
        />
      )}
      {previewTarget && (
        // ALPACA parity: Preview opens the SDMS spreadsheet DIRECTLY, read-only
        // and RIBBON-LESS (preview) — LIMS's Preview shows just the grid + sheet
        // tabs, no ribbon.  Open Package → SDMS keeps the full ribbon.
        <SdmsSpreadsheet
          preview
          batch={previewTarget as unknown as SdmsBatch}
          allBatches={(rows as AnalyticalBatchRow[]).map(b => ({
            id: b.id, batchId: b.batchId, test: b.test, method: b.method,
            matrix: b.matrix, templateId: b.templateID, templateName: b.templateName,
            jobIds: b.jobIds, statusText: b.statusText,
          }))}
          onClose={() => setPreviewTarget(null)}
          onRolledBack={() => { void load(); setPreviewTarget(null); }}
        />
      )}
      {pkgTarget && (
        <SdmsPackageDetail
          batch={pkgTarget as unknown as SdmsPackageRow}
          approval={isApproval}
          canWrite={perm.write}
          allBatches={(rows as AnalyticalBatchRow[]).map(b => ({
            id: b.id, batchId: b.batchId, test: b.test, method: b.method,
            matrix: b.matrix, templateId: b.templateID, templateName: b.templateName,
            jobIds: b.jobIds, statusText: b.statusText,
          }))}
          onClose={closePackage}
          onRefresh={load}
          onValidated={(whoCode, whenIso) => {
            // Optimistic update on the list row.  Detail overlay STAYS
            // open — the analyst reads the ALPACA-parity green "Samples
            // validated/approved successfully." banner + the in-place Status
            // flip in the detail, then closes on their own.  Approval keeps the
            // row (batch shown as SDMS Approved with its verify stamp).
            if (isApproval) applyApprove([pkgTarget.id], whoCode, whenIso);
            else            applyValidate([pkgTarget.id], whoCode, whenIso);
          }}
        />
      )}
    </div>
  );
}

/** Toolbar row on the grey page (BO UI Standard v2) — the shared <AppToolbar>.
 *  The form-specific Search By dropdown (Result Tracking + legacy Result
 *  Approval) and the Date Range picker ride in the toolbar's left slot; Refresh
 *  + Export to are the shared toolbar buttons.
 *
 *  SDMS Result Validation / Approval extend the left slot with a Preview button
 *  (XAF parity, DataReviewController); Rollback + Load live in the consolidated
 *  title row and Validate/Approve lives in <SdmsPackageDetail>. (Merged 2026-07-07.) */
function AnalysisToolbar({
  kind, loading,
  dateRange, setDateRange, searchBy, setSearchBy,
  onRetrieve, exportData,
  sdmsCanPreview, onSdmsPreview,
  onQcNew, onQcEdit, qcEditable, onQcDelete,
  onQcRollback, qcRollbackable, onQcSdms, qcHasSelection,
  onQueueNew, queueHasSelection,
  onPrepBatchEdit, onPrepBatchDelete, prepBatchHasSelection, prepBatchDeletable,
}: {
  kind: AnalysisKind; loading: boolean;
  dateRange: DateRangeKey; setDateRange: (k: DateRangeKey) => void;
  searchBy: 'QCBatchID' | 'JOBID'; setSearchBy: (s: 'QCBatchID' | 'JOBID') => void;
  onRetrieve: () => void; exportData: ExportData;
  sdmsCanPreview?: boolean;
  onSdmsPreview?:  () => void;
  // (Merged 2026-07-07) QC Batches list-view actions.
  onQcNew?: () => void;
  onQcEdit?: () => void; qcEditable?: boolean;
  onQcDelete?: () => void;
  onQcRollback?: () => void; qcRollbackable?: boolean;
  onQcSdms?: () => void; qcHasSelection?: boolean;
  // (Merged 2026-07-07) Analysis Queue → Create QC Batch.
  onQueueNew?: () => void; queueHasSelection?: boolean;
  // Sample Prep Batches list — Edit (open) / Delete (blocked once ResultCompleted).
  onPrepBatchEdit?: () => void; onPrepBatchDelete?: () => void;
  prepBatchHasSelection?: boolean; prepBatchDeletable?: boolean;
}) {
  const isSdms  = kind === 'result-validation' || kind === 'sdms-approval';  // (Merged 2026-07-07)
  const isQc    = kind === 'qc-batches';  // (Merged 2026-07-07)
  const isPrepBatches = kind === 'prep-batches';  // Sample Prep Batches list (Edit/Delete; New is queue-driven)
  const isQueue = kind === 'analysis-queue' || kind === 'prep-queue';  // prep-queue also opens a detail (Prep Batch)
  // Result Validation / Approval use the PLAIN (white-bg, orange-text) toolbar
  // dropdowns — matching Custom Reporting (user request 2026-07-09); other Analysis
  // forms keep the solid-orange .sa-date-sel.
  const isManualVal = kind === 'result-validation-manual' || kind === 'result-approval';
  const dateSelCls = `sa-date-sel${isManualVal ? ' sa-date-sel-plain' : ''}`;
  return (
    <>
      {/* The page heading is the shared <FormTitle> in the page header above
          (ONE heading per page — §18.6); this bar carries only the actions. */}
      {/* Shared toolbar — canonical order: [left] · Refresh · Export to.
          These read grids have no New/Edit/Delete; the Search By dropdown (when
          shown) and the Date Range picker ride in the left slot. */}
      <AppToolbar
        left={
          <>
            {/* Result Validation (manual) — the "Load" action moved to the TOP-RIGHT
                of the page header (Custom Reporting parity, user request 2026-07-09),
                so it's no longer in this toolbar. */}
            {/* Date Range/Filter is the FIRST toolbar action (user request
                2026-07-09; §18.6 form-specific buttons sit left of the rest).  On
                SDMS it reads "Date Filter" and precedes Preview; on QC Batches
                "Date Range" precedes New.  Shared <DateRangeSelect> (same
                component Custom Reporting uses) — a native <select> can't
                restyle its highlighted option (browser-blue); this renders the
                app's own orange-highlight popup instead (user report
                2026-07-17: SDMS Date Filter showed blue where Custom Reporting
                showed orange). */}
            {supportsDate(kind) && (
              <DateRangeSelect variant="dropdown" label={`Date ${isSdms ? 'Filter' : 'Range'}`}
                ranges={TRACKING_RANGES}
                value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
            )}
            {/* Result Tracking's primary action is the "Load" button in the TOP-RIGHT
                page header (Custom Reporting / Result Validation parity, §18.6) — NOT a
                toolbar button. See the dp-title-actions block above. */}
            {/* SDMS workflow action — Preview (select exactly 1 batch).  Rollback +
                Load sit in the consolidated title row; Validate/Approve lives in
                <SdmsPackageDetail>. (Merged 2026-07-07.) */}
            {isSdms && (
              <button className="ca-btn" onClick={onSdmsPreview} disabled={!sdmsCanPreview}
                title="Preview SDMS result image (select exactly 1 batch)">
                <i className="ti ti-eye" style={{ color: 'var(--ai-blue)' }} /> Preview
              </button>
            )}
            {/* (Merged 2026-07-07) Analysis Queue → Create QC Batch. Tick one
                test-method row, then New opens the QC Batch form in create mode
                for it (Job ID → Load → Sort → OK). Completes the create flow that
                the QC Batches list "New" button routes here for. */}
            {isQueue && (
              <button className="ca-btn" onClick={onQueueNew} disabled={!queueHasSelection}
                title={queueHasSelection
                  ? `Create a ${kind === 'prep-queue' ? 'prep' : 'QC'} batch for the selected test method`
                  : 'Select one test-method row first'}>
                <span className="tb-i tb-i-green">＋</span> New
              </button>
            )}
            {/* (Merged 2026-07-07) QC Batches list-view actions in the EXACT legacy
                order: New · Edit · Delete · Rollback · SDMS (before the shared
                Refresh / Export).  New → Analysis Queue; Edit/Delete gate to status
                In(0,1); Rollback to Not In(0,1); SDMS is deferred (info toast).
                Data Package is NOT here — it's the per-row grid hyperlink. */}
            {isQc && (
              <>
                <button className="ca-btn" onClick={onQcNew} title="New">
                  <span className="tb-i tb-i-green">＋</span> New
                </button>
                <button className="ca-btn" onClick={onQcEdit} disabled={!qcEditable} title="Edit">
                  <span className="tb-i tb-i-blue">✎</span> Edit
                </button>
                <button className="ca-btn" onClick={onQcDelete} disabled={!qcEditable} title="Delete">
                  <span className="tb-i tb-i-red">🗑</span> Delete
                </button>
                <button className="ca-btn" onClick={onQcRollback} disabled={!qcRollbackable}
                  title={qcRollbackable ? 'Rollback the selected batch' : 'Available once the batch is past Pending Submission'}>
                  <i className="ti ti-arrow-back-up" /> Rollback
                </button>
                <button className="ca-btn" onClick={onQcSdms} disabled={!qcHasSelection}
                  title="Open the SDMS sheet for the selected batch">
                  <i className="ti ti-table" /> SDMS
                </button>
              </>
            )}
            {/* Sample Prep Batches list-view actions — Edit · Delete (OG
                SamplePrepBatch_ListView).  New is a QUEUE action (Sample Prep
                Queue → New), so it is intentionally NOT here; Delete is blocked
                once the batch is ResultCompleted (statusCode 3). */}
            {isPrepBatches && (
              <>
                <button className="ca-btn" onClick={onPrepBatchEdit} disabled={!prepBatchHasSelection} title="Edit">
                  <span className="tb-i tb-i-blue">✎</span> Edit
                </button>
                <button className="ca-btn" onClick={onPrepBatchDelete} disabled={!prepBatchDeletable}
                  title={prepBatchHasSelection
                    ? (prepBatchDeletable ? 'Delete the selected prep batch' : 'A prep batch with completed results cannot be deleted')
                    : 'Select one prep batch first'}>
                  <span className="tb-i tb-i-red">🗑</span> Delete
                </button>
              </>
            )}
            {/* Search By dropdown (QCBatchID | JOBID) — Result Tracking, legacy
                job-level Result Approval, AND the manual Result Validation queue
                (ALPACA DataReviewQueryPanel's Search By + Retrieve).  The value
                drives the server SP's @SearchBy/@Search filter. */}
            {(kind === 'result-tracking' || kind === 'result-approval'
              || kind === 'result-validation-manual') && (
              <div className="sa-date-wrap" style={{ marginRight: 4 }}>
                <i className="ti ti-filter sa-date-prefix" />
                <span className="sa-date-label">Search By</span>
                <select className={dateSelCls} value={searchBy}
                  onChange={e => setSearchBy(e.target.value as 'QCBatchID' | 'JOBID')}>
                  <option value="QCBatchID">QCBatchID</option>
                  <option value="JOBID">JOBID</option>
                </select>
              </div>
            )}
          </>
        }
        onRefresh={onRetrieve}
        refreshing={loading}
        exportData={exportData}
      />
    </>
  );
}

// ── drill-down detail panel ────────────────────────────────────────────────
interface PrepDetailRow {
  sampleParameterId: string;
  sampleID:          string;
  jobId:             string | null;
  sampleNo:          number;
  collectDate:       string | null;
  dateReceived:      string | null;
  sampleLocation:    string | null;
  test:              string | null;
  method:            string | null;
  matrix:            string | null;
  parameter:         string | null;
  client:            string | null;
  projectId:         string | null;
  subOut:            boolean | null;
  // select-samples-then-load-into-QC-Batch — jobOid is the Job GUID
  // (Samplecheckin.Oid) qcBatchApi.load's jobOids expects; sampleLogInOid
  // (SampleLogIn.Oid) narrows Load to the picked samples. Populated only by
  // the analysis-queue detail endpoint; null on the prep-queue one.
  jobOid?:           string | null;
  sampleLogInOid?:   string | null;
}

/** Prep Queue "View details" popup — centered modal, SAME design as the Analysis
 *  Queue SampleDetailsModal (orange header + shared .list-search + sample grid).
 *  Read-only view of the pending-prep SampleParameter rows under one TestMethod
 *  (backed by prep-queue/{id}/samples). Close-only; it does not seed a batch. */
function PrepQueueDetailPanel({
  row, onClose, onLoadSelected,
}: { row: AnalysisQueueRow; onClose: () => void; onLoadSelected: (rows: PrepDetailRow[]) => void }) {
  const [detail, setDetail] = useState<PrepDetailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [sel, setSel]         = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/v1/analysis/prep-queue/${row.testMethodId}/samples`);
        if (!cancelled) setDetail((res.data as { data: PrepDetailRow[] }).data ?? []);
      } catch {
        if (!cancelled) setDetail([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [row.testMethodId]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return detail;
    return detail.filter(r =>
      `${r.jobId ?? ''} ${r.sampleID} ${r.parameter ?? ''} ${r.client ?? ''}`.toLowerCase().includes(term));
  }, [detail, search]);

  const toggle = (id: string) => setSel(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allChecked = filtered.length > 0 && filtered.every(r => sel.has(r.sampleParameterId));
  const toggleAll = () => setSel(prev => {
    if (allChecked) { const n = new Set(prev); filtered.forEach(r => n.delete(r.sampleParameterId)); return n; }
    const n = new Set(prev); filtered.forEach(r => n.add(r.sampleParameterId)); return n;
  });
  // OK → hand the ticked samples up so the Prep Batch form opens seeded with them.
  const handleOk = () => onLoadSelected(detail.filter(r => sel.has(r.sampleParameterId)));

  return (
    <div className="qcb-scrim" onClick={onClose}>
      <div className="pickmodal" style={{ width: 900 }} onClick={e => e.stopPropagation()}>
        <div className="mh">🧪 {row.test ?? 'Prep Queue'} — Samples <span className="x" onClick={onClose}>×</span></div>
        <div className="pb">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
            <button className="btn" onClick={() => exportCsv(filtered as unknown as Record<string, unknown>[], 'prep-queue-samples.csv')}>
              ⭳ Export to
            </button>
            <div className="list-search">
              <i className="ti ti-search" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>
          <div className="gridbox" style={{ borderRadius: 6 }}>
            <table style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th className="cbcol"><input type="checkbox" className="chk" checked={allChecked} onChange={toggleAll} /></th>
                  <th>JobID</th><th>SampleID</th><th>Parameter</th><th>Client</th><th>Collected</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={NO_DATA_CELL_STYLE}>Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} style={NO_DATA_CELL_STYLE}>No matches</td></tr>
                ) : filtered.map(r => (
                  <tr key={r.sampleParameterId} className={sel.has(r.sampleParameterId) ? 'sel' : undefined}>
                    <td className="cbcol"><input type="checkbox" className="chk" checked={sel.has(r.sampleParameterId)} onChange={() => toggle(r.sampleParameterId)} /></td>
                    <td>{r.jobId ?? '—'}</td>
                    <td>{r.sampleID}</td>
                    <td>{r.parameter ?? '—'}</td>
                    <td>{r.client ?? '—'}</td>
                    <td>{fmtDate(r.collectDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="foot">
            <span>Page 1 of 1 ({filtered.length} items){sel.size > 0 ? ` · Selected (${sel.size})` : ''}</span>
            <div className="pages"><span className="pg">‹</span><b>1</b><span className="pg">›</span></div>
            <div className="psize">Page size: <select><option>100</option></select></div>
          </div>
        </div>
        <div className="mf">
          <button className="btn primary" disabled={sel.size === 0} onClick={handleOk}>OK</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const NO_DATA_CELL_STYLE: CSSProperties = { padding: 20, textAlign: 'center', color: '#8a94a6' };

/** Analysis Queue's "Sample Details" popup — centered modal (QC-Batch-Prototype
 *  `#sdScrim`/`.pickmodal`), backed by its OWN endpoint (analysis-queue/{id}/
 *  samples → AI_Analysis_AnalysisQueueDetail_SP), NOT prep-queue's, since the two
 *  queues' sample-readiness criteria differ. Checkboxes are functional (legacy OG
 *  parity — QCBatchController.cs Dc_Accepting ~line 2788): OK with a selection
 *  seeds the QC Batch screen with exactly those samples (jobOids pre-filled) via
 *  onLoadSelected; OK with nothing selected, or Cancel, just closes. */
function SampleDetailsModal({
  row, onClose, onLoadSelected,
}: { row: AnalysisQueueRow; onClose: () => void; onLoadSelected: (rows: PrepDetailRow[]) => void }) {
  const [detail, setDetail]   = useState<PrepDetailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [sel, setSel]         = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/v1/analysis/analysis-queue/${row.testMethodId}/samples`);
        if (!cancelled) setDetail((res.data as { data: PrepDetailRow[] }).data ?? []);
      } catch {
        if (!cancelled) setDetail([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [row.testMethodId]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return detail;
    return detail.filter(r =>
      `${r.jobId ?? ''} ${r.sampleID} ${r.parameter ?? ''} ${r.client ?? ''}`.toLowerCase().includes(term));
  }, [detail, search]);

  const toggle = (id: string) => setSel(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allChecked = filtered.length > 0 && filtered.every(r => sel.has(r.sampleParameterId));
  const toggleAll = () => setSel(prev => {
    if (allChecked) { const next = new Set(prev); filtered.forEach(r => next.delete(r.sampleParameterId)); return next; }
    const next = new Set(prev);
    filtered.forEach(r => next.add(r.sampleParameterId));
    return next;
  });
  const handleOk = () => {
    const selectedRows = detail.filter(r => sel.has(r.sampleParameterId));
    if (selectedRows.length > 0) onLoadSelected(selectedRows);
    onClose();
  };

  return (
    <div className="qcb-scrim" onClick={onClose}>
      <div className="pickmodal" style={{ width: 900 }} onClick={e => e.stopPropagation()}>
        <div className="mh">🔬 {row.test ?? 'Analysis Queue'} — Samples <span className="x" onClick={onClose}>×</span></div>
        <div className="pb">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
            <button className="btn" onClick={() => exportCsv(filtered as unknown as Record<string, unknown>[], 'sample-details.csv')}>
              ⭳ Export to
            </button>
            <div className="list-search">
              <i className="ti ti-search" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>
          <div className="gridbox" style={{ borderRadius: 6 }}>
            <table style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th className="cbcol"><input type="checkbox" className="chk" checked={allChecked} onChange={toggleAll} /></th>
                  <th>JobID</th><th>SampleID</th><th>Parameter</th><th>Client</th><th>Collected</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={NO_DATA_CELL_STYLE}>Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} style={NO_DATA_CELL_STYLE}>No matches</td></tr>
                ) : filtered.map(r => (
                  <tr key={r.sampleParameterId} className={sel.has(r.sampleParameterId) ? 'sel' : undefined}>
                    <td className="cbcol"><input type="checkbox" className="chk" checked={sel.has(r.sampleParameterId)} onChange={() => toggle(r.sampleParameterId)} /></td>
                    <td>{r.jobId ?? '—'}</td>
                    <td>{r.sampleID}</td>
                    <td>{r.parameter ?? '—'}</td>
                    <td>{r.client ?? '—'}</td>
                    <td>{fmtDate(r.collectDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="foot">
            <span>Page 1 of 1 ({filtered.length} items){sel.size > 0 ? ` · Selected (${sel.size})` : ''}</span>
            <div className="pages"><span className="pg">‹</span><b>1</b><span className="pg">›</span></div>
            <div className="psize">Page size: <select><option>100</option></select></div>
          </div>
        </div>
        <div className="mf">
          <button className="btn primary" onClick={handleOk}>OK</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
